/**
 * State Tax Authority & Resolution Data
 * Single source of truth for all 50 state tax guide pages.
 *
 * Merged from:
 *   - 50-State verification summary (practitioner notes)
 *   - External research (Public Integrity, NCSL, state agency sites)
 *
 * Rules applied:
 *   - No phone numbers
 *   - No links to state taxing authority websites
 *   - Informational article links only (publicintegrity.org, ncsl.org)
 *   - Internal notes sanitized for public-facing content
 */

const stateData = [
  {
    name: "Alabama",
    abbreviation: "AL",
    slug: "state-income-tax-help-alabama",
    capital: "Montgomery",
    taxAuthority: "Alabama Department of Revenue",
    incomeType: "graduated",
    incomeTaxRange: "2% – 5%",
    salesTaxRange: "4% (+ local)",
    hasStateLevyPower: true,
    difficulty: "hard",
    coordinates: { lat: 32.806671, lng: -86.79113 },
    summary:
      "Alabama tax debts are handled by the Alabama Department of Revenue and can trigger state-level liens, levies, and wage garnishments if left unresolved. The state does not offer an Offer in Compromise program, so resolution strategy typically centers on installment plans, penalty relief, and hardship claims. Alabama's collection posture is notably aggressive and inflexible, with a demanding substantiation process for any modification requests.",
    keyFacts: [
      "No Offer in Compromise program — Alabama does not offer OIC",
      "Installment agreements available up to 24 months without financial disclosure",
      "No stated option for longer-term payment plans beyond 24 months",
      "Wage levies cannot be released — exceptions only for extreme hardship or corrected returns that eliminate the balance",
      "Wage levy modification is possible but requires full financial hardship documentation and 3 months of supporting documents",
      "Penalty abatement available via Form PWR — must include payment for principal and interest plus a Statement of Circumstances",
      "10-year collection statute from date of assessment",
      "Alabama is one of the few states that allows a federal income tax deduction on state returns",
    ],
    commonIssues: [
      "Aggressive collections with limited flexibility — the tax return review department demands full substantiation and is very difficult to negotiate with",
      "No OIC means taxpayers with large balances have fewer settlement options than at the federal level",
      "Wage garnishment is essentially permanent once in place — only corrected returns or extreme hardship qualify for release",
      "Penalty abatement requests require specific form filing with exact years specified and payment applied to principal and interest",
    ],
    oic: {
      available: false,
      formNumber: "",
      stopsCollections: false,
      notes:
        "Alabama does not offer an Offer in Compromise program. Resolution strategy focuses on payment plans and penalty relief.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 24,
      maxMonthsWithFinancials: 0,
      notes:
        "Up to 24 months without financials. No stated option for longer terms.",
    },
    garnishment: {
      canLift: false,
      canReduce: true,
      percentage: "Varies",
      notes:
        "No releases allowed except for extreme hardship or corrected returns that eliminate the balance. Modification requires full financial hardship documentation and all supporting docs for the last 3 months — no exceptions.",
    },
    penaltyAbatement: {
      available: true,
      notes:
        "File Form PWR with payment for principal and interest. Must include Statement of Circumstances specifying exact years.",
    },
    bankLevy: {
      canRelease: false,
      notes: "No release — very limited exceptions.",
    },
    hardship: { available: false, notes: "" },
    collectionAgency: null,
    statute: "10 years",
    recentChanges: [
      "Overtime pay exemption enacted for 2025-2028 (following federal provisions)",
      "Alabama remains one of few states allowing federal income tax deduction on state returns",
      "Standard deduction increased with inflation adjustments",
    ],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Alabama",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Alaska",
    abbreviation: "AK",
    slug: "state-income-tax-help-alaska",
    capital: "Juneau",
    taxAuthority: "Alaska Department of Revenue",
    incomeType: "none",
    incomeTaxRange: "No state income tax",
    salesTaxRange: "No state sales tax (local may apply)",
    hasStateLevyPower: false,
    difficulty: "none",
    coordinates: { lat: 64.200841, lng: -152.493782 },
    summary:
      "Alaska does not levy a state individual income tax. State tax issues are more commonly tied to business taxes such as sales/use, excise, and corporate taxes. Because there is no personal income tax, individual residents generally do not face state-level wage garnishments or levies for income tax debt. Federal tax obligations still apply fully to Alaska residents.",
    keyFacts: [
      "No state individual income tax",
      "No state sales tax (though local municipalities may impose sales taxes)",
      "State tax issues are typically business-related (corporate, excise, oil & gas)",
      "Federal tax obligations apply fully — IRS maintains collection authority regardless of state tax policy",
      "Alaska Permanent Fund Dividend may be subject to IRS levy for federal tax debts",
    ],
    commonIssues: [
      "Businesses operating in Alaska may face sales/use tax issues at the municipal level",
      "Federal tax debts remain the primary concern for individual residents",
      "Alaska Permanent Fund Dividend payments can be intercepted by the IRS for unpaid federal taxes",
    ],
    oic: null,
    installmentAgreement: null,
    garnishment: null,
    penaltyAbatement: null,
    bankLevy: null,
    hardship: null,
    collectionAgency: null,
    statute: "N/A",
    recentChanges: [],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Alaska",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Arizona",
    abbreviation: "AZ",
    slug: "state-income-tax-help-arizona",
    capital: "Phoenix",
    taxAuthority: "Arizona Department of Revenue",
    incomeType: "flat",
    incomeTaxRange: "2.5%",
    salesTaxRange: "5.6% (+ local)",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 34.048927, lng: -111.093735 },
    summary:
      "Arizona tax debts are handled by the Arizona Department of Revenue and can trigger state-level liens, levies, and wage garnishments. The state offers both short-term and long-term installment plans — up to 72 months — without requiring financial disclosure, making it one of the more flexible states for payment arrangements. An Offer in Compromise is technically available but acceptance rates are extremely low.",
    keyFacts: [
      "OIC technically available but acceptance rate is extremely low",
      "Short-term payment plan available up to 12 months",
      "Long-term payment plan available up to 72 months — no financials required for either",
      "6-year collection statute from date of assessment",
      "2023: Transitioned to a 2.5% flat tax from graduated rates — one of the lowest flat rates nationally",
      "Collections aggressiveness is generally low compared to other states",
    ],
    commonIssues: [
      "OIC applications are almost always denied — professional guidance is critical before investing time in this path",
      "Despite low collections aggressiveness, the state will still pursue liens and levies on unresolved balances",
      "Flat tax transition in 2023 may cause confusion for taxpayers filing amended returns for prior graduated-rate years",
    ],
    oic: {
      available: true,
      formNumber: "",
      stopsCollections: false,
      notes:
        "Technically available but acceptance rate is extremely low. Not recommended as a primary strategy.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 72,
      maxMonthsWithFinancials: 72,
      notes:
        "Short-term plan up to 12 months. Long-term plan up to 72 months. Financials not needed for either.",
    },
    garnishment: {
      canLift: false,
      canReduce: false,
      percentage: "25%",
      notes: "",
    },
    penaltyAbatement: { available: true, notes: "" },
    bankLevy: { canRelease: false, notes: "" },
    hardship: { available: false, notes: "" },
    collectionAgency: null,
    statute: "6 years",
    recentChanges: [
      "2023: Transitioned to 2.5% flat tax from graduated rates — one of the lowest flat rates nationally",
    ],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Arizona",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Arkansas",
    abbreviation: "AR",
    slug: "state-income-tax-help-arkansas",
    capital: "Little Rock",
    taxAuthority: "Arkansas Department of Finance and Administration",
    incomeType: "graduated",
    incomeTaxRange: "2% – 4.4%",
    salesTaxRange: "6.5% (+ local)",
    hasStateLevyPower: true,
    difficulty: "easy",
    coordinates: { lat: 34.799999, lng: -92.199997 },
    summary:
      "Arkansas tax debts are handled by the Department of Finance and Administration and can trigger liens, levies, and wage garnishments. The state offers an Offer in Compromise program and installment agreements up to 24 months without financials. Collections aggressiveness is generally low, but wage garnishments are particularly difficult to resolve — once in place, they cannot be converted to a payment plan.",
    keyFacts: [
      "OIC available via Form 2000-4 — all tax returns must be on file",
      "Installment agreements available for 12-24 months without financials",
      "Supervisor review required for IAs exceeding 24 months — IRS Form 433-A accepted as financial statement",
      "Temporary 6-month plan available to stave off collections while waiting for returns to post (must cover interest)",
      "Once a wage garnishment is in place, it CANNOT be switched to a payment plan — must pay garnished year(s) in full or ride out the garnishment",
      "Wage garnishments set at 25%",
      "5-year collection statute from date of assessment",
    ],
    commonIssues: [
      "Wage garnishments are essentially irreversible — once in place, the only options are paying the garnished years in full or waiting it out",
      "Taxpayers should prioritize getting on a payment plan before a garnishment is issued",
      "The 6-month temporary plan is valuable for buying time while returns are being processed",
    ],
    oic: {
      available: true,
      formNumber: "Form 2000-4",
      stopsCollections: false,
      notes: "All tax returns must be on file before applying.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 24,
      maxMonthsWithFinancials: 36,
      notes:
        "12-24 months without financials. Supervisor review for IAs over 24 months — IRS Form 433-A accepted. Temporary 6-month plan available to stave off collections while returns post.",
    },
    garnishment: {
      canLift: false,
      canReduce: false,
      percentage: "25%",
      notes:
        "Once a garnishment is in place, it CANNOT be switched to a payment plan. Options: pay the garnished year(s) in full, or ride out the garnishment and get on a plan before the next one hits.",
    },
    penaltyAbatement: { available: true, notes: "" },
    bankLevy: { canRelease: false, notes: "" },
    hardship: { available: false, notes: "" },
    collectionAgency: null,
    statute: "5 years",
    recentChanges: [
      "2023-2025: Major rate reduction — top rate cut from 4.9% to 4.4%, targeting sub-4% flat rate",
      "Corporate rate also reduced (5.3% to 4.8%)",
    ],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Arkansas",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "California",
    abbreviation: "CA",
    slug: "state-income-tax-help-california",
    capital: "Sacramento",
    taxAuthority: "California Franchise Tax Board (FTB)",
    incomeType: "graduated",
    incomeTaxRange: "1% – 13.3%",
    salesTaxRange: "7.25% (+ local)",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 36.778259, lng: -119.417931 },
    summary:
      "California tax debts are handled by the Franchise Tax Board (FTB) and can trigger aggressive state-level liens, levies, and wage garnishments. The FTB operates independently from the IRS with a 20-year collection statute — double the federal window. An OIC program exists but is significantly harder than the IRS equivalent, and wage garnishments can only be modified, never fully lifted. California has the highest state income tax rate in the nation at 13.3%.",
    keyFacts: [
      "OIC available via Form 4905 — significantly harder than IRS OIC",
      "FTB evaluates ability to pay, asset equity, present/future income, and age (younger applicants less likely to be approved)",
      "Only 2 OIC submissions allowed per liability — may require 5-year collateral agreement on future income",
      "OIC does NOT automatically stop collections — $50 application fee, 4-6 month decision timeline",
      "Installment agreements up to 36 months without financials (unless balance exceeds $25K)",
      "Up to 60 months with financials — Form 3567 required, must be direct debit, $34 setup fee",
      "Wage garnishments can only be modified/reduced — CANNOT be fully lifted (exception: existing child support garnishment)",
      "Temporary hardship deferral available for 6-12 months with verbal financials",
      "Permanent hardship possible for elderly or fixed-income taxpayers",
      "20-year collection statute from assessment — double the IRS's 10 years",
      "FTB Final Notice triggers collection action approximately 45 days later",
      "13.3% top rate (including millionaire surtax) is the highest state income tax in the nation",
    ],
    commonIssues: [
      "The 20-year collection statute means California debts persist far longer than federal debts",
      "Wage garnishments are permanent modifications — taxpayers should prioritize getting on a plan before garnishment is issued",
      "The FTB OIC process is substantially more difficult than the IRS — collateral agreements can require 5 years of future income",
      "Balances over $25K require full financial disclosure regardless of requested payment plan term",
      "Multiple California agencies (FTB, EDD, CDTFA) may each have separate collection actions",
      "TOP 500 / license holds require full compliance, IA with financials, or hardship qualification",
    ],
    oic: {
      available: true,
      formNumber: "Form 4905",
      stopsCollections: false,
      notes:
        "Harder than IRS OIC. Only 2 submissions per liability. May require 5-year collateral agreement. $50 fee. All returns must be filed. Does NOT automatically stop collections. 4-6 month timeline.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 36,
      maxMonthsWithFinancials: 60,
      notes:
        "Up to 36 months without financials (unless balance over $25K). Up to 60 months with financials (Form 3561). Form 3567 required — must be direct debit. First two payments made manually. $34 setup fee.",
    },
    garnishment: {
      canLift: false,
      canReduce: true,
      percentage: "Varies",
      notes:
        "Can only be modified/reduced — CANNOT be lifted. Only exception: if a child support garnishment is already in place.",
    },
    penaltyAbatement: {
      available: true,
      notes:
        "One-time 30-day payment delay available on installment agreements.",
    },
    bankLevy: {
      canRelease: false,
      notes:
        "Cannot apply for IA online if levy is in place — must contact the state directly.",
    },
    hardship: {
      available: true,
      notes:
        "Temporary deferral 6-12 months with verbal financials. Permanent hardship possible for elderly or fixed-income taxpayers.",
    },
    collectionAgency: null,
    statute: "20 years",
    recentChanges: [
      "2025: LA wildfire disaster relief — automatic extensions for affected counties",
      "Millionaire surtax (4% on income over $1M, total 13.3%) remains highest in nation",
      "FTB OIC now available online via MyFTB account",
      "Multi-agency OIC application available for taxpayers owing multiple CA agencies",
    ],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#California",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Colorado",
    abbreviation: "CO",
    slug: "state-income-tax-help-colorado",
    capital: "Denver",
    taxAuthority: "Colorado Department of Revenue",
    incomeType: "flat",
    incomeTaxRange: "4.4%",
    salesTaxRange: "2.9% (+ local)",
    hasStateLevyPower: true,
    difficulty: "hard",
    coordinates: { lat: 39.113014, lng: -105.358887 },
    summary:
      "Colorado tax debts are handled by the Colorado Department of Revenue and can trigger liens, levies, and wage garnishments. The state offers installment agreements up to 99 months with financials, but OIC eligibility has a unique requirement: the IRS must have already accepted an OIC before Colorado will consider one. Once a wage garnishment is issued, installment agreements are no longer available, making early intervention critical.",
    keyFacts: [
      "OIC available BUT only if the IRS has already accepted an OIC — IRS Form 433-A can be used as financial statement",
      "Installment agreements up to 36 months without financials",
      "Up to 99 months with financials and manager approval (Form DR 6596)",
      "Direct debit not required — payments can be made online or by mail",
      "Missing returns do NOT have to be filed to establish an IA (unusual)",
      "Once a wage garnishment is issued, IA is NO LONGER available",
      "Garnishment modification requires Forms DR 6596 & DR 6597 with pay stubs and bank statements",
      "6-year collection statute from filing date",
    ],
    commonIssues: [
      "The IRS-first OIC requirement makes Colorado settlements rare — you must have a federal OIC accepted before the state will consider one",
      "Wage garnishment eliminates the IA option entirely — taxpayers must act before garnishment is issued",
      "The 99-month extended plan is generous but requires manager approval and full financial documentation",
      "Colorado's TABOR refunds may be offset against outstanding tax debts",
    ],
    oic: {
      available: true,
      formNumber: "",
      stopsCollections: false,
      notes:
        "Only available if the IRS has already accepted an OIC. IRS Form 433-A can be used as the financial statement.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 36,
      maxMonthsWithFinancials: 99,
      notes:
        "Up to 99 months with financials and manager approval. Form DR 6596. Direct debit not required. Missing returns do NOT have to be filed to establish an IA.",
    },
    garnishment: {
      canLift: false,
      canReduce: true,
      percentage: "Varies",
      notes:
        "Once garnishment is issued, IA is NO LONGER available. To modify: complete Forms DR 6596 & DR 6597 with two pay stubs, three months of bank statements, and modification proposal amount.",
    },
    penaltyAbatement: { available: true, notes: "" },
    bankLevy: { canRelease: false, notes: "" },
    hardship: { available: false, notes: "" },
    collectionAgency: null,
    statute: "6 years",
    recentChanges: [
      "2024-2025: Flat rate reduced to 4.4% (from 4.55%) via Proposition 121",
      "TABOR refunds continue (Colorado refunds excess state revenue to taxpayers)",
    ],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Colorado",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Connecticut",
    abbreviation: "CT",
    slug: "state-income-tax-help-connecticut",
    capital: "Hartford",
    taxAuthority: "Connecticut Department of Revenue Services",
    incomeType: "graduated",
    incomeTaxRange: "2% – 6.99%",
    salesTaxRange: "6.35%",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 41.599998, lng: -72.699997 },
    summary:
      "Connecticut tax debts are handled by the Department of Revenue Services and can trigger liens, levies, and wage garnishments. The state offers both OIC and installment plans, but neither will stop or lift existing garnishments or levies. Connecticut has no collection statute of limitations, meaning debts persist indefinitely. Garnishment reduction requires a special hearing process.",
    keyFacts: [
      "OIC available via Form 656a — will NOT stop collections, must be compliant with all returns",
      "Installment agreements up to 12 months without financials",
      "Up to 36 months with full financials, 3 months bank statements, and proof of income (Form C&E-120a)",
      "Temporary plans available even with Substitute for Returns (SFRs)",
      "Payment plans will NOT lift garnishment or levy",
      "Garnishment reduction requires: first garnishment payment received, wage reduction request from employer, and a special hearing",
      "NO collection statute of limitations — debts persist indefinitely",
    ],
    commonIssues: [
      "No collection statute means Connecticut tax debts never expire — they persist until paid or settled",
      "Garnishment reduction is unusually complex — requires a formal hearing process, not just a phone call",
      "Neither OIC nor installment agreements will stop existing collection actions",
      "The 36-month plan requires extensive financial documentation including bank statements and income proof",
    ],
    oic: {
      available: true,
      formNumber: "Form 656a",
      stopsCollections: false,
      notes: "Will NOT stop collections. Must be compliant with all returns.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 12,
      maxMonthsWithFinancials: 36,
      notes:
        "36 months requires Form C&E-120a plus 3 months bank statements and proof of income. Temporary plans available with SFRs. Payment plan will NOT lift garnishment or levy.",
    },
    garnishment: {
      canLift: false,
      canReduce: true,
      percentage: "25%",
      notes:
        "Reduction requires: first garnishment payment must be received by the state, then wage reduction request completed via employer, then special hearing to argue for reduction.",
    },
    penaltyAbatement: { available: true, notes: "" },
    bankLevy: { canRelease: false, notes: "" },
    hardship: { available: false, notes: "" },
    collectionAgency: null,
    statute: "No limit",
    recentChanges: [],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Connecticut",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Delaware",
    abbreviation: "DE",
    slug: "state-income-tax-help-delaware",
    capital: "Dover",
    taxAuthority: "Delaware Division of Revenue",
    incomeType: "graduated",
    incomeTaxRange: "2.2% – 6.6%",
    salesTaxRange: "No state sales tax",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 39.0, lng: -75.5 },
    summary:
      "Delaware tax debts are handled by the Division of Revenue and can trigger liens, levies, and wage garnishments. The state does not offer an Offer in Compromise program. Installment agreements are available up to 48 months, with plans over 48 months triggering a judgment filing. Delaware has no state sales tax, so tax issues are primarily income-tax related.",
    keyFacts: [
      "No Offer in Compromise program — Delaware does not offer OIC",
      "Installment agreements up to 24 months without requiring auto-debit or judgment filing",
      "Plans of 24-48 months require automatic bank deduction or voluntary wage deduction to avoid a tax lien filing",
      "Plans over 48 months will result in a judgment being filed to protect the state's interest",
      "Written requests accepted — include tax bill copy, name, TIN, and tax period",
      "Hardship relief possible — contact the state directly",
      "No state sales tax — tax issues are primarily income-tax related",
    ],
    commonIssues: [
      "No OIC means limited settlement options — resolution focuses on payment plans and hardship relief",
      "Plans exceeding 48 months trigger formal judgment filings, which impact credit and public records",
      "The 24-month threshold for auto-debit requirements can be a barrier for taxpayers preferring manual payments",
    ],
    oic: {
      available: false,
      formNumber: "",
      stopsCollections: false,
      notes: "Delaware does not offer OIC.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 48,
      maxMonthsWithFinancials: 48,
      notes:
        "Up to 24 months without auto-debit. 24-48 months requires automatic bank deduction. Over 48 months triggers judgment filing.",
    },
    garnishment: {
      canLift: false,
      canReduce: false,
      percentage: "Varies",
      notes: "",
    },
    penaltyAbatement: { available: true, notes: "" },
    bankLevy: { canRelease: false, notes: "" },
    hardship: {
      available: true,
      notes: "Hardship relief possible — contact the state.",
    },
    collectionAgency: null,
    statute: "Unknown",
    recentChanges: [],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Delaware",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Florida",
    abbreviation: "FL",
    slug: "state-income-tax-help-florida",
    capital: "Tallahassee",
    taxAuthority: "Florida Department of Revenue",
    incomeType: "none",
    incomeTaxRange: "No state income tax",
    salesTaxRange: "6% (+ local)",
    hasStateLevyPower: false,
    difficulty: "none",
    coordinates: { lat: 27.994402, lng: -81.760254 },
    summary:
      "Florida does not levy a state individual income tax. State tax issues are more commonly tied to business taxes including sales/use tax, corporate income tax, and excise taxes. The Florida Department of Revenue is notably aggressive on sales tax collections for businesses. Individual residents primarily face federal tax obligations, and the IRS maintains full collection authority regardless of Florida's lack of state income tax.",
    keyFacts: [
      "No state individual income tax — constitutionally prohibited",
      "State tax issues are primarily business-related (sales/use, corporate, excise)",
      "Florida DOR is aggressive on sales tax collections for businesses",
      "Federal tax obligations apply fully — IRS maintains collection authority",
      "6% state sales tax plus local surtaxes",
    ],
    commonIssues: [
      "Businesses face aggressive sales tax enforcement from the Florida DOR",
      "Individuals relocating to Florida for tax purposes still face federal obligations on all income",
      "Sales tax audit exposure for businesses with nexus in Florida",
    ],
    oic: null,
    installmentAgreement: null,
    garnishment: null,
    penaltyAbatement: null,
    bankLevy: null,
    hardship: null,
    collectionAgency: null,
    statute: "N/A",
    recentChanges: [],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Florida",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Georgia",
    abbreviation: "GA",
    slug: "state-income-tax-help-georgia",
    capital: "Atlanta",
    taxAuthority: "Georgia Department of Revenue",
    incomeType: "flat",
    incomeTaxRange: "5.49%",
    salesTaxRange: "4% (+ local)",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 33.247875, lng: -83.441162 },
    summary:
      "Georgia tax debts are handled by the Department of Revenue and can trigger liens, levies, wage garnishments, and referral to private collection agencies. The state offers an OIC program but it will not stop collections during review. Installment agreements are available up to 60 months without financials. The state is moderately aggressive and uses private collection agencies — once a debt is assigned to an agency, taxpayers must deal with the agency directly.",
    keyFacts: [
      "OIC available via Form OIC-1 — will NOT stop collections",
      "$100 non-refundable application fee for OIC",
      "Three OIC grounds: Doubt as to Collectibility, Doubt as to Liability, Economic Hardship",
      "Financial statement Form CD-14C required for OIC applications",
      "6-8 weeks for DOR to acknowledge OIC receipt",
      "Installment agreements available up to 60 months without financials via Georgia Tax Center",
      "IA will NOT lift levy or garnishment — must be compliant with all returns",
      "First-Time Penalty Abatement available over phone when setting up IA — but state will NOT consider penalty waiver if there is any outstanding liability or missing returns (must pay in full first)",
      "After recording a state tax execution (Georgia's term for lien), the state adds a 20% fee",
      "10-year collection statute from date tax execution is recorded",
      "Uses private collection agencies — once assigned, must deal with agency, not DOR directly",
      "2024: Flat 5.49% rate (from graduated 1%-5.75%), targeting 4.99% by 2029",
    ],
    commonIssues: [
      "The penalty abatement catch-22: the state requires full payment before considering any penalty waiver, which defeats the purpose for most taxpayers",
      "Once a debt is assigned to a private collection agency, it cannot be returned to the DOR — taxpayers are stuck dealing with the agency",
      "The 20% fee added upon recording a tax execution significantly increases the total amount owed",
      "OIC does not stop collections — taxpayers may face continued enforcement while their application is pending",
    ],
    oic: {
      available: true,
      formNumber: "Form OIC-1",
      stopsCollections: false,
      notes:
        "Will NOT stop collections. $100 non-refundable fee. Three grounds: Doubt as to Collectibility, Liability, or Economic Hardship. Form CD-14C (financial statement) required. 6-8 weeks for acknowledgment.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 60,
      maxMonthsWithFinancials: 60,
      notes:
        "60 months without financials via Georgia Tax Center. IA will NOT lift levy or garnishment. Must be compliant with all returns.",
    },
    garnishment: {
      canLift: false,
      canReduce: false,
      percentage: "Varies",
      notes:
        "IA will not lift garnishment. State adds 20% fee when recording tax execution (lien).",
    },
    penaltyAbatement: {
      available: true,
      notes:
        "First-Time Penalty Abatement available over phone when setting up IA. However, DOR will NOT consider penalty waiver if there is any outstanding liability or missing returns — must pay in full first.",
    },
    bankLevy: { canRelease: false, notes: "" },
    hardship: { available: false, notes: "" },
    collectionAgency: {
      name: "Various private agencies",
      notes:
        "Once assigned to collection agency, must deal with agency directly — cannot return debt to DOR.",
    },
    statute: "10 years",
    recentChanges: [
      "2024: Flat 5.49% income tax rate (from graduated 1%-5.75%) — major structural change",
      "Rate targeting 4.99% by 2029",
    ],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Georgia",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },

  {
    name: "Hawaii",
    abbreviation: "HI",
    slug: "state-income-tax-help-hawaii",
    capital: "Honolulu",
    taxAuthority: "Hawaii Department of Taxation",
    incomeType: "graduated",
    incomeTaxRange: "1.4% – 11%",
    salesTaxRange: "4% GET (+ county surcharge)",
    hasStateLevyPower: true,
    difficulty: "hard",
    coordinates: { lat: 21.094318, lng: -157.498337 },
    summary:
      "Hawaii tax debts are handled by the Department of Taxation and can trigger liens, levies, and wage garnishments. The state technically offers an OIC but acceptance is nearly impossible. Payment plan terms are entirely case-by-case with no published standards — each case is assigned to an individual collector who sets the terms. Hawaii has no collection statute of limitations, meaning debts persist indefinitely. Wage garnishments are set at a high 35% rate.",
    keyFacts: [
      "OIC technically available but nearly impossible to get accepted",
      "Installment agreement terms are entirely case-by-case — each case assigned to an individual collector",
      "No published standard terms for payment plans",
      "Wage garnishments generally set at 35%",
      "Garnishment reduction possible with financial condition statement, 2 months of pay stubs, and 2 bank statements",
      "Conditional penalty waiver available — requires lump sum payment",
      "NO collection statute of limitations — debts persist indefinitely",
      "Hawaii uses a General Excise Tax (GET) instead of traditional sales tax — applies to nearly all business transactions",
    ],
    commonIssues: [
      "The lack of standardized payment plan terms means outcomes depend heavily on which collector is assigned to the case",
      "The 35% garnishment rate is one of the highest nationally and severely impacts take-home pay",
      "No collection statute means Hawaii debts never expire — there is no waiting it out",
      "OIC is essentially unavailable in practice despite being technically offered",
    ],
    oic: {
      available: true,
      formNumber: "",
      stopsCollections: false,
      notes:
        "Technically available but nearly impossible to get accepted. Not a viable strategy.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 0,
      maxMonthsWithFinancials: 0,
      notes:
        "Entirely case-by-case. Each case assigned to an individual collector who sets terms. No published standards.",
    },
    garnishment: {
      canLift: false,
      canReduce: true,
      percentage: "35%",
      notes:
        "Submit financial condition statement plus last 2 months of pay stubs and 2 bank statements for reduction consideration.",
    },
    penaltyAbatement: {
      available: true,
      notes: "Conditional waiver available — requires lump sum payment.",
    },
    bankLevy: { canRelease: false, notes: "" },
    hardship: { available: false, notes: "" },
    collectionAgency: null,
    statute: "No limit",
    recentChanges: [],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Hawaii",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Idaho",
    abbreviation: "ID",
    slug: "state-income-tax-help-idaho",
    capital: "Boise",
    taxAuthority: "Idaho State Tax Commission",
    incomeType: "flat",
    incomeTaxRange: "5.8%",
    salesTaxRange: "6%",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 44.240459, lng: -114.478828 },
    summary:
      "Idaho tax debts are handled by the Idaho State Tax Commission and can trigger liens, levies, and wage garnishments. Limited resolution details are publicly available — the state's OIC availability, payment plan terms, and collection statute are not well documented. Idaho transitioned to a 5.8% flat income tax rate in 2023.",
    keyFacts: [
      "OIC availability unknown — limited public information",
      "Payment plan terms not well documented publicly",
      "Collection statute details unclear",
      "2023: Transitioned to 5.8% flat rate from graduated brackets",
      "Collections aggressiveness is generally low",
    ],
    commonIssues: [
      "Limited publicly available information about resolution options means professional representation is especially valuable",
      "Taxpayers should contact the state directly or work with a representative to determine available options",
    ],
    oic: {
      available: false,
      formNumber: "",
      stopsCollections: false,
      notes: "OIC availability is unknown — limited public information.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 0,
      maxMonthsWithFinancials: 0,
      notes: "Terms not well documented publicly. Contact the state directly.",
    },
    garnishment: null,
    penaltyAbatement: { available: true, notes: "" },
    bankLevy: null,
    hardship: null,
    collectionAgency: null,
    statute: "Unknown",
    recentChanges: ["2023: Moved to 5.8% flat rate from graduated brackets"],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Idaho",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Illinois",
    abbreviation: "IL",
    slug: "state-income-tax-help-illinois",
    capital: "Springfield",
    taxAuthority: "Illinois Department of Revenue",
    incomeType: "flat",
    incomeTaxRange: "4.95%",
    salesTaxRange: "6.25% (+ local)",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 40.0, lng: -89.0 },
    summary:
      "Illinois tax debts are handled by the Department of Revenue and can trigger liens, levies, and wage garnishments at 15% of gross wages with only 10 days' notice. The state offers an OIC through the Board of Appeals, but decisions are final with no appeal. A powerful strategy exists: filing a Board of Appeals petition allows requesting a Temporary Restraining Order that freezes all collection activity for years while the case is pending. Illinois uses private collection agencies including Linebarger Goggan Blair & Sampson.",
    keyFacts: [
      "OIC available via Form BOA-1 filed with the Board of Appeals — only considers 'uncertainty as to collectibility'",
      "Board of Appeals decisions are FINAL — no appeal available",
      "Installment agreements 12-24 months without financials",
      "Over 24 months or balances over $10K require full financials (Forms EG-13i or EG-13b & CPP-1) regardless of term",
      "Wage garnishments are 15% of gross wages with only 10 days' notice",
      "Garnishment reduction requires letter of hardship, last 3 pay stubs, completed EG-13i, and full compliance",
      "TRO Strategy: Filing a BOA petition allows requesting a Temporary Restraining Order that freezes ALL collection activity — cases take years, collections paused during that time",
      "Liens filed in public Tax Lien Registry (searchable online) and last 20 years",
      "Uses Linebarger Goggan Blair & Sampson (LGBS) as collection agency",
      "LGBS terms: 6-12 month IA without financials, extended plans require most current 1040 and last 2 pay stubs",
    ],
    commonIssues: [
      "The 10-day garnishment notice window is one of the shortest nationally — rapid response is critical",
      "Balances over $10K require full financials regardless of the requested payment plan length",
      "BOA decisions are final with no appeal — the OIC application is essentially a one-shot opportunity",
      "The TRO strategy is powerful but requires understanding the Board of Appeals process",
      "Once a debt is with LGBS, a 1/3 down payment is recommended — lower DP requires a letter explaining desired terms",
    ],
    oic: {
      available: true,
      formNumber: "Form BOA-1",
      stopsCollections: false,
      notes:
        "Filed with Board of Appeals. Only considers 'uncertainty as to collectibility.' Decisions are FINAL — no appeal. Oral hearing available but not required.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 24,
      maxMonthsWithFinancials: 36,
      notes:
        "12-24 months without financials. Over 24 months or over $10K requires financials (Forms EG-13i or EG-13b & CPP-1).",
    },
    garnishment: {
      canLift: false,
      canReduce: true,
      percentage: "15% of gross",
      notes:
        "Only 10-day notice before garnishment starts. Reduction requires: letter of hardship, last 3 pay stubs, completed EG-13i, and full compliance.",
    },
    penaltyAbatement: {
      available: true,
      notes:
        "Via Board of Appeals (Form BOA-1). Sales tax penalties typically NOT eligible.",
    },
    bankLevy: {
      canRelease: false,
      notes: "Bank holds funds 20 days before remitting.",
    },
    hardship: { available: false, notes: "" },
    collectionAgency: {
      name: "Linebarger Goggan Blair & Sampson (LGBS)",
      notes:
        "6-12 month IA without financials. Extended plan requires most current 1040 plus last 2 pay stubs. 1/3 down payment recommended.",
    },
    statute: "20 years",
    recentChanges: [
      "Rate unchanged at 4.95%",
      "Increased enforcement on gig workers and online sellers",
    ],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Illinois",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Indiana",
    abbreviation: "IN",
    slug: "state-income-tax-help-indiana",
    capital: "Indianapolis",
    taxAuthority: "Indiana Department of Revenue",
    incomeType: "flat",
    incomeTaxRange: "3.05%",
    salesTaxRange: "7%",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 39.849426, lng: -86.258278 },
    summary:
      "Indiana tax debts are handled by the Department of Revenue and can trigger liens, levies, and wage garnishments. The state is one of the more taxpayer-friendly for resolution — the OIC program will stop collections while under review, and installment agreements are available up to 24 months without financials. For plans exceeding 24 months, the Taxpayer Advocate can arrange hardship installment agreements. Indiana uses United Collections Bureau as its third-party collection agency.",
    keyFacts: [
      "OIC available via Form FSOIC — WILL stop collections while under review",
      "All returns must be on file for OIC consideration",
      "Installment agreements up to 24 months without financials",
      "Plans exceeding 24 months require Taxpayer Advocate hardship application with full financials",
      "Collection agency (United Collections Bureau): case-by-case IAs, temp 6-12 month plans, up to 36 months",
      "Bank levy will not be released unless extenuating circumstances (e.g., corrected returns eliminate balance)",
      "Rate reduced from 3.23% to 3.05% in 2024, further reductions planned",
      "Uses license holds and refund intercepts as enforcement tools",
    ],
    commonIssues: [
      "The Taxpayer Advocate path for 24+ month plans requires full financial disclosure and documentation",
      "Bank levies are very difficult to release — typically only if corrected returns eliminate the balance entirely",
      "United Collections Bureau terms are case-by-case, so outcomes can vary",
      "Residency disputes require at least 3 items of proof (rental deed, voter registration, driver's license, vehicle registration, tax return, or utility bill)",
    ],
    oic: {
      available: true,
      formNumber: "Form FSOIC",
      stopsCollections: true,
      notes: "WILL stop collections. All returns must be on file.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 24,
      maxMonthsWithFinancials: 36,
      notes:
        "24 months without financials. Over 24 months requires Taxpayer Advocate hardship IA with full financials.",
    },
    garnishment: {
      canLift: false,
      canReduce: false,
      percentage: "Varies",
      notes: "",
    },
    penaltyAbatement: { available: true, notes: "" },
    bankLevy: {
      canRelease: false,
      notes:
        "Will not release unless extenuating circumstances (e.g., corrected returns eliminate balance).",
    },
    hardship: {
      available: true,
      notes:
        "Taxpayer Advocate can arrange hardship IAs for plans exceeding 24 months.",
    },
    collectionAgency: {
      name: "United Collections Bureau",
      notes:
        "Case-by-case IAs. Temp 6-12 month plans available. Up to 36 months.",
    },
    statute: "Unknown",
    recentChanges: [
      "Rate reduced from 3.23% to 3.05% in 2024, further reductions planned",
    ],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Indiana",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Iowa",
    abbreviation: "IA",
    slug: "state-income-tax-help-iowa",
    capital: "Des Moines",
    taxAuthority: "Iowa Department of Revenue",
    incomeType: "flat",
    incomeTaxRange: "3.8%",
    salesTaxRange: "6% (+ local)",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 42.011539, lng: -93.210526 },
    summary:
      "Iowa tax debts are handled by the Iowa Department of Revenue and can trigger liens, levies, and wage garnishments. Limited resolution details are publicly available for Iowa's specific programs. The state is undergoing a major tax simplification — phasing to a 3.8% flat rate by 2026 and fully exempting retirement income. Collections aggressiveness is generally low.",
    keyFacts: [
      "OIC availability unknown — limited public information",
      "Payment plan terms not well documented publicly",
      "Collection statute details unclear",
      "2023-2026: Phasing to 3.8% flat rate from graduated brackets",
      "Retirement income fully exempt from state tax starting 2023",
      "Collections aggressiveness is generally low",
    ],
    commonIssues: [
      "Limited publicly available resolution information — professional representation recommended",
      "The ongoing tax reform means prior-year returns may have different rate structures than current year",
    ],
    oic: {
      available: false,
      formNumber: "",
      stopsCollections: false,
      notes: "OIC availability unknown — limited public information.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 0,
      maxMonthsWithFinancials: 0,
      notes: "Terms not well documented publicly. Contact the state directly.",
    },
    garnishment: null,
    penaltyAbatement: { available: true, notes: "" },
    bankLevy: null,
    hardship: null,
    collectionAgency: null,
    statute: "Unknown",
    recentChanges: [
      "2023-2026: Phasing to 3.8% flat rate from graduated brackets",
      "Retirement income fully exempt from state tax starting 2023",
    ],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Iowa",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Kansas",
    abbreviation: "KS",
    slug: "state-income-tax-help-kansas",
    capital: "Topeka",
    taxAuthority: "Kansas Department of Revenue",
    incomeType: "graduated",
    incomeTaxRange: "3.1% – 5.7%",
    salesTaxRange: "6.5% (+ local)",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 38.5, lng: -98.0 },
    summary:
      "Kansas tax debts are handled by the Department of Revenue and can trigger liens, levies, and wage garnishments. The state offers a settlement option called a Petition for Abatement and installment agreements up to 12 months without financials. Kansas uses third-party collection agencies and has no specific collection statute of limitations — debts persist indefinitely.",
    keyFacts: [
      "Settlement option called 'Petition for Abatement' — functions like an OIC",
      "Installment agreements up to 12 months without financials",
      "Plans over 12 months require Form CE-3 plus 3 pay stubs and 3 months of bank statements",
      "Uses third-party collection agencies",
      "No specific collection statute — debts persist indefinitely",
    ],
    commonIssues: [
      "No collection statute means Kansas tax debts never expire",
      "The 12-month limit without financials is relatively short — many taxpayers will need to provide full documentation",
      "Third-party collection agencies may have different terms than the state directly",
    ],
    oic: {
      available: true,
      formNumber: "Petition for Abatement",
      stopsCollections: false,
      notes: "Called a 'Petition for Abatement' in Kansas.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 12,
      maxMonthsWithFinancials: 24,
      notes:
        "Up to 12 months without financials. Over 12 months requires Form CE-3 plus 3 pay stubs and 3 months bank statements.",
    },
    garnishment: null,
    penaltyAbatement: { available: true, notes: "" },
    bankLevy: null,
    hardship: null,
    collectionAgency: { name: "Various third-party agencies", notes: "" },
    statute: "No limit",
    recentChanges: [],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Kansas",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Kentucky",
    abbreviation: "KY",
    slug: "state-income-tax-help-kentucky",
    capital: "Frankfort",
    taxAuthority: "Kentucky Department of Revenue",
    incomeType: "flat",
    incomeTaxRange: "4%",
    salesTaxRange: "6%",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 37.839333, lng: -84.27002 },
    summary:
      "Kentucky tax debts are handled by the Department of Revenue and can trigger liens, levies, wage garnishments, license holds, and refund intercepts. The state offers an OIC program and installment agreements up to 24 months without financials. A unique aspect of Kentucky's collection statute: while balances don't drop off, 10 years after the original bill the state stops active collection — still capturing refunds but no longer pursuing bank levies or license suspensions.",
    keyFacts: [
      "OIC available — details on application process handled case-by-case",
      "Installment agreements up to 24 months without financials",
      "Extended IA available with manager approval",
      "Penalty waiver requires paying pure tax and interest IN FULL before applying",
      "License/registration holds can be lifted by establishing IA, sending first payment, and paying $40 reinstatement fee (3-5 business days)",
      "License hold can be lifted even with SFRs and combination of non-filings",
      "Collection statute: balances don't drop off, but 10 years after original bill the state stops active collection",
      "After 10 years: state still captures refunds but no bank levies or license suspensions — voluntary payments accepted",
      "Rate reduced to 4% (from 4.5%), further reductions possible if revenue targets met",
    ],
    commonIssues: [
      "The penalty waiver requirement to pay in full first makes it inaccessible for most taxpayers with large balances",
      "The 10-year passive collection threshold is helpful for older debts but the state still captures refunds indefinitely",
      "License holds are a significant enforcement tool but can be lifted relatively quickly with an IA",
    ],
    oic: {
      available: true,
      formNumber: "",
      stopsCollections: false,
      notes: "Available — terms handled case-by-case.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 24,
      maxMonthsWithFinancials: 36,
      notes: "24 months without financials. Extended IA with manager approval.",
    },
    garnishment: null,
    penaltyAbatement: {
      available: true,
      notes:
        "Client must pay pure tax and interest IN FULL before applying for penalty waiver.",
    },
    bankLevy: null,
    hardship: null,
    collectionAgency: null,
    statute: "10 years (then passive collection only)",
    recentChanges: [
      "Rate reduced to 4% (from 4.5%), further reductions possible if revenue targets met",
    ],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Kentucky",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Louisiana",
    abbreviation: "LA",
    slug: "state-income-tax-help-louisiana",
    capital: "Baton Rouge",
    taxAuthority: "Louisiana Department of Revenue",
    incomeType: "flat",
    incomeTaxRange: "3%",
    salesTaxRange: "4.45% (+ local)",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 31.169546, lng: -91.867805 },
    summary:
      "Louisiana tax debts are handled by the Department of Revenue and can trigger liens, levies, wage garnishments, and driver's license suspensions. The state offers an OIC program but it will not stop collections. Installment agreements are available up to 60 months with financials and a 20% down payment. Bank levies will not be released under any circumstances. Louisiana underwent a major tax overhaul in 2025, moving to a flat 3% rate.",
    keyFacts: [
      "OIC available via Form R20212A — will NOT stop collections",
      "Installment agreements up to 24 months without financials",
      "Up to 60 months with financials (Form R19026) — requires 20% down payment and $105 IA fee",
      "Bank levy will NOT be released — no exceptions",
      "Wage garnishment can be reduced with full financials — must fit within 60-month criteria",
      "Driver's license hold: IA can lift the hold, must be current on most recent 3 years of filings, SFRs OK on older years",
      "Harris & Harris (collection agency): balance over $1K triggers license suspension, 20% DP (flexible to 10%), $105 fee, 23-month IA",
      "15-year statute for tax liens",
      "2025: MAJOR tax overhaul — flat 3% rate (from graduated 1.85%-4.25%), elimination of most deductions/exemptions",
    ],
    commonIssues: [
      "Bank levies are permanent — no release available, making early intervention on levies impossible after the fact",
      "The 20% down payment for extended plans is a significant barrier for cash-strapped taxpayers",
      "License suspensions can be triggered on balances as low as $1K when referred to Harris & Harris",
      "The 2025 flat rate overhaul is one of the biggest state tax changes nationally and will affect filing for years",
    ],
    oic: {
      available: true,
      formNumber: "Form R20212A",
      stopsCollections: false,
      notes: "Will NOT stop collections.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 24,
      maxMonthsWithFinancials: 60,
      notes: "60 months requires Form R19026, 20% down payment, and $105 fee.",
    },
    garnishment: {
      canLift: false,
      canReduce: true,
      percentage: "Varies",
      notes:
        "Reduction requires full financials. IA must fit within 60-month criteria.",
    },
    penaltyAbatement: { available: true, notes: "" },
    bankLevy: {
      canRelease: false,
      notes: "Will NOT release bank levy — no exceptions.",
    },
    hardship: null,
    collectionAgency: {
      name: "Harris & Harris",
      notes:
        "Balance over $1K triggers license suspension. 20% DP (flexible to 10% lump sum). $105 fee. 23-month IA.",
    },
    statute: "15 years",
    recentChanges: [
      "2025: MAJOR overhaul — flat 3% rate (from graduated 1.85%-4.25%), elimination of most deductions/exemptions",
    ],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Louisiana",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Maine",
    abbreviation: "ME",
    slug: "state-income-tax-help-maine",
    capital: "Augusta",
    taxAuthority: "Maine Revenue Services",
    incomeType: "graduated",
    incomeTaxRange: "5.8% – 7.15%",
    salesTaxRange: "5.5%",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 45.367584, lng: -68.972168 },
    summary:
      "Maine tax debts are handled by Maine Revenue Services and can trigger liens, levies, and wage garnishments. Resolution options include installment agreements and possibly an OIC, though details are limited. A notable feature is that bank levy modifications are possible — unlike many states. The installment agreement without financials is limited to just 6 months. Collections aggressiveness is generally low.",
    keyFacts: [
      "OIC possibly available — limited public information",
      "Installment agreements up to 6 months without financials",
      "Bank levy modification possible — contact the state directly",
      "Collection statute details unclear",
      "Collections aggressiveness is generally low",
    ],
    commonIssues: [
      "The 6-month limit without financials is very short — most taxpayers will need to provide financial documentation for longer plans",
      "Limited public information about resolution options makes professional representation valuable",
    ],
    oic: {
      available: true,
      formNumber: "",
      stopsCollections: false,
      notes: "Possibly available — limited public information.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 6,
      maxMonthsWithFinancials: 12,
      notes: "6 months without financials.",
    },
    garnishment: null,
    penaltyAbatement: null,
    bankLevy: {
      canRelease: true,
      notes: "Modification possible — contact the state directly.",
    },
    hardship: null,
    collectionAgency: null,
    statute: "Unknown",
    recentChanges: [],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Maine",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Maryland",
    abbreviation: "MD",
    slug: "state-income-tax-help-maryland",
    capital: "Annapolis",
    taxAuthority: "Comptroller of Maryland",
    incomeType: "graduated",
    incomeTaxRange: "2% – 5.75% (+ county)",
    salesTaxRange: "6%",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 39.045753, lng: -76.641273 },
    summary:
      "Maryland tax debts are handled by the Comptroller of Maryland and can trigger liens, levies, wage garnishments, and license holds. The state offers OIC and installment plans up to 99 months with financials. Maryland has no collection statute of limitations — debts persist indefinitely. The state is unique in collecting both state and county income taxes simultaneously, and uses multiple collection agencies with different terms. Penalty abatement is strictly one-time-only and requires full principal payment first.",
    keyFacts: [
      "OIC available via Forms MD-433a & MD-656 — will NOT stop collections",
      "Liabilities not eligible for OIC until 2 years after date of assessment",
      "SFRs (Substitute for Returns) are OK for OIC applications",
      "Installment agreements up to 36 months without financials",
      "Up to 99 months with financials — 20% down payment required",
      "Business installment agreements require 25% down payment",
      "Down payment can be paid in installments UNLESS there's a license hold (then full DP needed before license lift)",
      "Wage garnishment can be reduced but CANNOT be switched into a payment plan",
      "Penalty abatement: must pay PRINCIPAL on ALL years first — strictly one-time-only",
      "If penalty abatement requested on some years but not others, unpaid years lose future waiver eligibility",
      "Collection agencies: Penn Credit (full payment in 30 days) and Harris & Harris (12-18 month IAs, balance over $5K requires DP)",
      "NO collection statute — debts persist indefinitely",
      "Unique: dual state + county income tax collection system",
    ],
    commonIssues: [
      "No collection statute means Maryland debts never expire",
      "The 2-year waiting period for OIC eligibility after assessment delays settlement options",
      "Penalty abatement is truly one-time — if you don't request it for all years simultaneously, you lose eligibility on the remaining years",
      "Wage garnishment cannot be converted to a payment plan — only reduced",
      "Penn Credit (collection agency) demands full payment in 30 days with no flexibility",
      "The dual state/county tax system creates additional complexity for resolution",
    ],
    oic: {
      available: true,
      formNumber: "Forms MD-433a & MD-656",
      stopsCollections: false,
      notes:
        "Will NOT stop collections. Liabilities not eligible until 2 years after assessment. SFRs OK.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 36,
      maxMonthsWithFinancials: 99,
      notes:
        "99 months requires 20% DP. Business: 25% DP. DP can be paid in installments unless license hold exists. For reduced DP, full MD433B required.",
    },
    garnishment: {
      canLift: false,
      canReduce: true,
      percentage: "Varies",
      notes: "Can be reduced but CANNOT be switched into a payment plan.",
    },
    penaltyAbatement: {
      available: true,
      notes:
        "Must pay PRINCIPAL on ALL years first. Strictly one-time-only. If requested on some years but not others, unpaid years lose future eligibility.",
    },
    bankLevy: { canRelease: false, notes: "" },
    hardship: null,
    collectionAgency: {
      name: "Penn Credit / Harris & Harris",
      notes:
        "Penn Credit: full payment in 30 days. Harris & Harris: 12-18 month IAs, balance over $5K requires DP.",
    },
    statute: "No limit",
    recentChanges: [],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Maryland",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Massachusetts",
    abbreviation: "MA",
    slug: "state-income-tax-help-massachusetts",
    capital: "Boston",
    taxAuthority: "Massachusetts Department of Revenue",
    incomeType: "flat",
    incomeTaxRange: "5% (+ 4% surtax over $1M)",
    salesTaxRange: "6.25%",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 42.407211, lng: -71.382439 },
    summary:
      "Massachusetts tax debts are handled by the Department of Revenue and can trigger liens, levies, and wage garnishments. The state is notably more flexible than many — it will set up installment agreements even with SFRs, and levies CAN be released with an IA and first payment (rare among states). OIC is available but will not stop collections. The 2023 millionaire surtax of 4% on income over $1M (total 9%) is driving significant audit activity.",
    keyFacts: [
      "OIC available via Forms M656 & M433I — will NOT stop collections",
      "Installment agreements up to 36 months without financials",
      "Over 36 months requires Form M433I with 3 months bank statements and full income/expense documentation",
      "State WILL set up IAs with SFRs (Substitute for Returns) — unusual flexibility",
      "Bank levy CAN be released with an IA and first payment — one of the few states that allows this",
      "2023: 4% surtax on income over $1M (total effective rate 9%) — driving significant audit activity",
      "Collections aggressiveness is generally low",
    ],
    commonIssues: [
      "The millionaire surtax is triggering increased audit and enforcement activity on high-income taxpayers",
      "While levy release is possible, the first IA payment must be made — have funds ready",
      "The SFR flexibility is valuable for taxpayers who haven't filed but want to get into a payment plan",
    ],
    oic: {
      available: true,
      formNumber: "Forms M656 & M433I",
      stopsCollections: false,
      notes: "Will NOT stop collections.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 36,
      maxMonthsWithFinancials: 60,
      notes:
        "36 months without financials. Over 36 months requires Form M433I with 3 months bank statements and all income/expenses. State WILL set up IAs with SFRs.",
    },
    garnishment: {
      canLift: true,
      canReduce: true,
      percentage: "Varies",
      notes: "Levy release IS possible with IA and first payment.",
    },
    penaltyAbatement: { available: true, notes: "" },
    bankLevy: {
      canRelease: true,
      notes: "Release possible with IA and first payment.",
    },
    hardship: null,
    collectionAgency: null,
    statute: "Unknown",
    recentChanges: [
      "2023: 4% surtax on income over $1M (total 9%) — driving significant audit activity",
    ],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Massachusetts",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },

  {
    name: "Michigan",
    abbreviation: "MI",
    slug: "state-income-tax-help-michigan",
    capital: "Lansing",
    taxAuthority: "Michigan Department of Treasury",
    incomeType: "flat",
    incomeTaxRange: "4.25%",
    salesTaxRange: "6%",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 44.314844, lng: -85.602364 },
    summary:
      "Michigan tax debts are handled by the Department of Treasury and can trigger liens, levies, and wage garnishments. The state is relatively cooperative — the OIC program will stop collections while under review, and installment agreements are available up to 24 months without financials. Michigan uses MARCS as its collection agency, which offers its own IA terms. A key quirk: the state wants the first levy payment to be made before it will consider a payment plan alternative.",
    keyFacts: [
      "OIC available via Form 5181 — WILL stop collections while under review",
      "Installment agreements up to 24 months without financials",
      "Extended IA with admin review requires financials (Forms 3189 & 990)",
      "Collection agency (MARCS): 24 months without financials, beyond 24 requires Forms 3189 & 990 plus last 3 months pay stubs",
      "Garnishment: state wants the first levy payment made, THEN will consider payment plan in lieu of continued levy",
      "Rate returned to 4.25% (was temporarily 4.05% in 2023)",
    ],
    commonIssues: [
      "The state's requirement to make the first levy payment before considering a plan means taxpayers lose at least one payment to the levy",
      "MARCS collection agency terms differ from the state's — taxpayers should clarify which entity holds their case",
      "Extended plans beyond 24 months require substantial documentation through admin review",
    ],
    oic: {
      available: true,
      formNumber: "Form 5181",
      stopsCollections: true,
      notes: "WILL stop collections while under review.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 24,
      maxMonthsWithFinancials: 36,
      notes:
        "24 months without financials. Extended IA via admin review requires Forms 3189 & 990.",
    },
    garnishment: {
      canLift: false,
      canReduce: false,
      percentage: "Varies",
      notes:
        "State wants first levy payment made, THEN will consider payment plan in lieu of continued levy.",
    },
    penaltyAbatement: { available: true, notes: "" },
    bankLevy: {
      canRelease: false,
      notes:
        "First levy payment must be made before state considers alternatives.",
    },
    hardship: null,
    collectionAgency: {
      name: "MARCS",
      notes:
        "24 months without financials. Beyond 24 requires Forms 3189 & 990 plus last 3 months pay stubs.",
    },
    statute: "Unknown",
    recentChanges: ["Rate returned to 4.25% (temporarily 4.05% in 2023)"],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Michigan",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Minnesota",
    abbreviation: "MN",
    slug: "state-income-tax-help-minnesota",
    capital: "Saint Paul",
    taxAuthority: "Minnesota Department of Revenue",
    incomeType: "graduated",
    incomeTaxRange: "5.35% – 9.85%",
    salesTaxRange: "6.875% (+ local)",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 46.39241, lng: -94.63623 },
    summary:
      "Minnesota tax debts are handled by the Department of Revenue and can trigger liens, levies, and wage garnishments set at 25% of net pay. The state offers an OIC program and case-by-case installment agreements, but IAs will NOT lift wage garnishments. Garnishment reduction is possible by submitting a written request with pay documentation. Liens last 10 years but can be extended, and the state has no specific collection statute of limitations.",
    keyFacts: [
      "OIC available via Form C58C",
      "Installment agreements are entirely case-by-case — both with and without financials",
      "IA will NOT lift wage garnishment",
      "Wage garnishments set at 25% of net pay",
      "Garnishment reduction: submit last pay stub plus written request specifying desired payment amount",
      "Liens last 10 years but can be extended",
      "No specific collection statute — debts persist",
      "Rates range from 5.35% to 9.85% — among the highest state rates nationally",
    ],
    commonIssues: [
      "IAs will not lift garnishments — taxpayers must pursue garnishment reduction separately",
      "Case-by-case payment plan terms mean outcomes vary significantly by collector and circumstances",
      "The 9.85% top rate makes Minnesota one of the highest-tax states, increasing the stakes of resolution",
      "Lien extensions mean even the 10-year lien period isn't a reliable endpoint",
    ],
    oic: {
      available: true,
      formNumber: "Form C58C",
      stopsCollections: false,
      notes: "",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 0,
      maxMonthsWithFinancials: 0,
      notes: "Case-by-case basis. IA will NOT lift garnishment.",
    },
    garnishment: {
      canLift: false,
      canReduce: true,
      percentage: "25% of net",
      notes:
        "Send last pay stub plus written request specifying desired payment amount.",
    },
    penaltyAbatement: { available: true, notes: "" },
    bankLevy: { canRelease: false, notes: "" },
    hardship: null,
    collectionAgency: null,
    statute: "No limit (liens 10 years, extendable)",
    recentChanges: ["Rates unchanged (5.35%-9.85%)"],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Minnesota",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Mississippi",
    abbreviation: "MS",
    slug: "state-income-tax-help-mississippi",
    capital: "Jackson",
    taxAuthority: "Mississippi Department of Revenue",
    incomeType: "flat",
    incomeTaxRange: "4.7%",
    salesTaxRange: "7%",
    hasStateLevyPower: true,
    difficulty: "hard",
    coordinates: { lat: 32.741646, lng: -89.678696 },
    summary:
      "Mississippi tax debts are handled by the Department of Revenue and can trigger liens, levies, and wage garnishments. This is one of the most difficult states for tax resolution. Wage garnishments are fixed at 25% with absolutely no reduction available. Bank levies are continuous and will not be released under any circumstances — a bank levy can be issued even if the taxpayer is already on a wage garnishment. Garnishments can technically be switched to an IA but require a significant down payment.",
    keyFacts: [
      "OIC available — limited details on process",
      "Installment agreements are case-by-case — if case is with a collector, must follow that collector's guidelines",
      "Wage garnishment fixed at 25% — NO reduction available",
      "Garnishment CAN be switched to an IA but requires 1/3 down payment of balance",
      "Down payment may be reduced based on collector discretion and client history",
      "Bank levy is CONTINUOUS — will NOT be released, IA will NOT release it",
      "Bank account levy can happen EVEN IF client is already on a wage garnishment (and vice versa)",
      "Settlement possible but principal balance MUST be paid in full — only a portion of penalties and interest may be abated",
      "Voluntary payments applied to oldest debt first (not specifically to principal)",
      "Phasing to flat 4.7% rate, eliminating first $10K bracket",
    ],
    commonIssues: [
      "The combination of unreducible 25% garnishment and continuous bank levy makes Mississippi among the most aggressive collection states",
      "Simultaneous garnishment AND bank levy is possible — both can be active at the same time",
      "The 1/3 down payment to convert garnishment to IA is a major barrier for most taxpayers",
      "Settlements require full principal payment — only penalties and interest are partially negotiable",
      "Collector-dependent terms mean outcomes vary significantly",
    ],
    oic: {
      available: true,
      formNumber: "",
      stopsCollections: false,
      notes:
        "Available but limited details. Settlement requires full principal payment — only P&I partially negotiable.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 0,
      maxMonthsWithFinancials: 0,
      notes: "Case-by-case, dependent on assigned collector's guidelines.",
    },
    garnishment: {
      canLift: false,
      canReduce: false,
      percentage: "25%",
      notes:
        "25% fixed — NO reduction. Can switch to IA but requires 1/3 down payment. Bank levy can happen even while on garnishment.",
    },
    penaltyAbatement: { available: false, notes: "" },
    bankLevy: {
      canRelease: false,
      notes:
        "Continuous levy. Will NOT release. IA will NOT release. Levy can happen even if on garnishment simultaneously.",
    },
    hardship: null,
    collectionAgency: null,
    statute: "Unknown",
    recentChanges: [
      "Phasing to flat 4.7% rate, eliminating first $10K bracket",
    ],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Mississippi",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Missouri",
    abbreviation: "MO",
    slug: "state-income-tax-help-missouri",
    capital: "Jefferson City",
    taxAuthority: "Missouri Department of Revenue",
    incomeType: "graduated",
    incomeTaxRange: "2% – 4.8%",
    salesTaxRange: "4.225% (+ local)",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 38.456085, lng: -92.288368 },
    summary:
      "Missouri tax debts are handled by the Missouri Department of Revenue and can trigger liens, levies, and wage garnishments. Limited resolution details are publicly available for Missouri's specific programs. Collections aggressiveness is generally low. The top rate has been reduced to 4.8%.",
    keyFacts: [
      "OIC availability unknown — limited public information",
      "Payment plan terms not well documented publicly",
      "Collection statute details unclear",
      "Collections aggressiveness is generally low",
      "Top rate reduced to 4.8%",
    ],
    commonIssues: [
      "Limited publicly available resolution information — professional representation recommended",
      "Taxpayers should contact the state or work with a representative to clarify available options",
    ],
    oic: null,
    installmentAgreement: null,
    garnishment: null,
    penaltyAbatement: null,
    bankLevy: null,
    hardship: null,
    collectionAgency: null,
    statute: "Unknown",
    recentChanges: ["Rate reduced to 4.8%"],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Missouri",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Montana",
    abbreviation: "MT",
    slug: "state-income-tax-help-montana",
    capital: "Helena",
    taxAuthority: "Montana Department of Revenue",
    incomeType: "graduated",
    incomeTaxRange: "4.7% – 5.9%",
    salesTaxRange: "No state sales tax",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 46.879682, lng: -110.362566 },
    summary:
      "Montana tax debts are handled by the Department of Revenue and can trigger liens, levies, and wage garnishments. The state does NOT offer an OIC program. Installment agreements are available up to 36 months without financials with management approval. Montana is more flexible than many states on garnishments — they have expiration dates, and the state will switch to an IA even if a garnishment is in place. Bank levy modifications are possible. However, penalty abatement is only available within 30 days of the first notice.",
    keyFacts: [
      "OIC NOT available — Montana does not offer Offer in Compromise",
      "Installment agreements up to 36 months without financials (management approval required)",
      "Beyond 36 months: full financial condition form plus 3 months bank statements, pay stubs, and proof of expenses",
      "IAs available even with SFRs (Substitute for Returns)",
      "Wage garnishments have an expiration date — once expired, IA is available",
      "State will switch to IA even if garnishment is currently in place (unusual flexibility)",
      "Bank levy modifications possible — state may take only 20% of deposited wages",
      "Penalty abatement only available within 30 days of first notice — tight deadline",
      "No state sales tax — tax issues are primarily income-tax related",
    ],
    commonIssues: [
      "No OIC means no settlement option — resolution focuses on payment plans and waiting out garnishment expirations",
      "The 30-day penalty abatement window is very narrow — missing it means losing the option entirely",
      "Management approval for 36-month plans adds an extra step that can delay setup",
      "Bank levy modifications require documentation (pay stubs and bank statements)",
    ],
    oic: {
      available: false,
      formNumber: "",
      stopsCollections: false,
      notes: "Montana does not offer OIC.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 36,
      maxMonthsWithFinancials: 60,
      notes:
        "36 months requires management approval. Beyond 36 months requires full financials plus 3 months bank statements, pay stubs, and proof of expenses. IAs available with SFRs.",
    },
    garnishment: {
      canLift: true,
      canReduce: true,
      percentage: "Varies",
      notes:
        "Garnishments have expiration dates. State will switch to IA even if garnishment is in place.",
    },
    penaltyAbatement: {
      available: true,
      notes: "Only available within 30 days of first notice.",
    },
    bankLevy: {
      canRelease: true,
      notes:
        "Modifications possible — state may take only 20% of deposited wages. Requires pay stubs and bank statements.",
    },
    hardship: null,
    collectionAgency: null,
    statute: "Unknown",
    recentChanges: [],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Montana",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Nebraska",
    abbreviation: "NE",
    slug: "state-income-tax-help-nebraska",
    capital: "Lincoln",
    taxAuthority: "Nebraska Department of Revenue",
    incomeType: "graduated",
    incomeTaxRange: "2.46% – 5.84%",
    salesTaxRange: "5.5% (+ local)",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 41.12537, lng: -98.268082 },
    summary:
      "Nebraska tax debts are handled by the Department of Revenue and can trigger liens, levies, and wage garnishments. The state offers an OIC program and installment agreements, though the without-financials term is limited to just 3 months. Nebraska is undergoing significant tax reform — Social Security is fully exempt starting 2024 and rates are being phased down toward a 3.99% flat rate by 2027.",
    keyFacts: [
      "OIC available — must include proposed settlement amount, reasoning, and Financial Statement (Form FSIND-NE)",
      "Must be fully compliant with all returns for OIC consideration",
      "Installment agreements up to 3 months without financials",
      "Over 3 months: Form 27D (Payment and Authorization Agreement) for EFT",
      "Penalty abatement via Form 21 — Request for Abatement of Penalty",
      "Social Security fully exempt from state tax starting 2024",
      "Phased rate reductions targeting 3.99% flat rate by 2027",
    ],
    commonIssues: [
      "The 3-month limit without financials is one of the shortest nationally — most taxpayers will need to provide documentation",
      "OIC applications require a compelling case for why the settlement is in the state's best interest",
    ],
    oic: {
      available: true,
      formNumber: "Form FSIND-NE",
      stopsCollections: false,
      notes:
        "Must include proposed amount, reasoning why it's in state's best interest, and financial statement. Must be fully compliant.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 3,
      maxMonthsWithFinancials: 12,
      notes:
        "3 months without financials. Over 3 months: Form 27D for EFT setup.",
    },
    garnishment: null,
    penaltyAbatement: {
      available: true,
      notes: "Form 21 — Request for Abatement of Penalty.",
    },
    bankLevy: null,
    hardship: null,
    collectionAgency: null,
    statute: "Unknown",
    recentChanges: [
      "Social Security fully exempt from state tax starting 2024",
      "Phased rate reductions targeting 3.99% flat rate by 2027",
    ],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Nebraska",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Nevada",
    abbreviation: "NV",
    slug: "state-income-tax-help-nevada",
    capital: "Carson City",
    taxAuthority: "Nevada Department of Taxation",
    incomeType: "none",
    incomeTaxRange: "No state income tax",
    salesTaxRange: "6.85% (+ local)",
    hasStateLevyPower: false,
    difficulty: "none",
    coordinates: { lat: 38.313515, lng: -117.055374 },
    summary:
      "Nevada does not levy a state individual income tax. State tax issues are more commonly tied to business taxes including sales/use tax, the Commerce Tax on businesses with Nevada gross revenue exceeding $4 million, and the Modified Business Tax on wages. Individual residents primarily face federal tax obligations.",
    keyFacts: [
      "No state individual income tax",
      "State tax issues primarily business-related (sales/use, Commerce Tax, Modified Business Tax)",
      "Federal tax obligations apply fully — IRS maintains collection authority",
      "6.85% state sales tax plus local additions — among the highest combined rates when including local",
    ],
    commonIssues: [
      "Businesses face sales tax complexity with varying local rates across jurisdictions",
      "Federal tax debts remain the primary concern for individual Nevada residents",
      "Relocating to Nevada does not eliminate existing tax obligations to prior states of residence",
    ],
    oic: null,
    installmentAgreement: null,
    garnishment: null,
    penaltyAbatement: null,
    bankLevy: null,
    hardship: null,
    collectionAgency: null,
    statute: "N/A",
    recentChanges: [],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Nevada",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "New Hampshire",
    abbreviation: "NH",
    slug: "state-income-tax-help-new-hampshire",
    capital: "Concord",
    taxAuthority: "New Hampshire Department of Revenue Administration",
    incomeType: "none",
    incomeTaxRange: "No state income tax",
    salesTaxRange: "No state sales tax",
    hasStateLevyPower: false,
    difficulty: "none",
    coordinates: { lat: 43.452492, lng: -71.563896 },
    summary:
      "New Hampshire does not levy a state individual income tax or a state sales tax. The Interest & Dividends Tax was fully repealed in 2025, making New Hampshire one of the few states with no broad-based income or sales tax. State tax issues are primarily tied to business taxes (Business Profits Tax and Business Enterprise Tax). Individual residents primarily face federal tax obligations.",
    keyFacts: [
      "No state individual income tax",
      "No state sales tax",
      "Interest & Dividends Tax fully repealed in 2025",
      "Business Profits Tax (7.5%) and Business Enterprise Tax (0.55%) still apply to businesses",
      "Federal tax obligations apply fully — IRS maintains collection authority",
    ],
    commonIssues: [
      "Businesses still face state-level tax obligations through BPT and BET",
      "Federal tax debts remain the primary concern for individual residents",
      "The 2025 I&D Tax repeal eliminates the last form of individual income taxation",
    ],
    oic: null,
    installmentAgreement: null,
    garnishment: null,
    penaltyAbatement: null,
    bankLevy: null,
    hardship: null,
    collectionAgency: null,
    statute: "N/A",
    recentChanges: [
      "2025: Interest & Dividends Tax fully repealed — zero individual income tax",
    ],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#New-Hampshire",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "New Jersey",
    abbreviation: "NJ",
    slug: "state-income-tax-help-new-jersey",
    capital: "Trenton",
    taxAuthority: "New Jersey Division of Taxation",
    incomeType: "graduated",
    incomeTaxRange: "1.4% – 10.75%",
    salesTaxRange: "6.625%",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 40.298904, lng: -74.521011 },
    summary:
      "New Jersey tax debts are handled by the Division of Taxation and can trigger liens, levies, and wage garnishments at 25%. The state offers a Closing Agreement (their version of OIC) that will stop collections, and installment plans up to 36 months without financials. Liens last 20 years. A notable feature: the state will only accept the pure tax (base balance) for settlement — no negotiation on the tax amount itself. Pioneer is the state's collection agency.",
    keyFacts: [
      "OIC (called 'Closing Agreement') available — WILL stop collections",
      "State will only accept pure tax (base balance) for settlement — penalties and interest may be negotiated",
      "No appeals on closing agreements, but can resubmit with a higher offer",
      "Installment agreements up to 36 months without financials (Deferred Payment Plan Request form)",
      "Over 36 months: written hardship request with supporting docs (3 months bank statements, all income/expenses)",
      "IA will NOT lift garnishment or levy",
      "Wage garnishments set at 25%",
      "Collection agency (Pioneer): call for IA terms, penalty abatement available with written statement",
      "Liens last 20 years",
    ],
    commonIssues: [
      "The pure-tax-only settlement basis means the state won't reduce the actual tax owed — only penalties and interest are negotiable",
      "Installment agreements will not stop existing garnishments or levies",
      "The Closing Agreement process has no appeal — if rejected, the only option is resubmitting with a higher amount",
      "10.75% top rate is among the highest nationally, increasing the stakes of resolution",
    ],
    oic: {
      available: true,
      formNumber: "Closing Agreement",
      stopsCollections: true,
      notes:
        "WILL stop collections. State only accepts pure tax (base balance) for settlement. No appeals but can resubmit with higher offer.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 36,
      maxMonthsWithFinancials: 60,
      notes:
        "36 months without financials. Over 36 months: written hardship request plus supporting docs. IA will NOT lift garnishment or levy.",
    },
    garnishment: {
      canLift: false,
      canReduce: false,
      percentage: "25%",
      notes: "IA will NOT lift garnishment.",
    },
    penaltyAbatement: {
      available: true,
      notes:
        "Available through Pioneer collection agency with written statement.",
    },
    bankLevy: { canRelease: false, notes: "" },
    hardship: null,
    collectionAgency: {
      name: "Pioneer",
      notes:
        "Call Pioneer for IA terms. Penalty abatement available with written statement.",
    },
    statute: "20 years",
    recentChanges: ["Rates unchanged (1.4%-10.75%)"],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#New-Jersey",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "New Mexico",
    abbreviation: "NM",
    slug: "state-income-tax-help-new-mexico",
    capital: "Santa Fe",
    taxAuthority: "New Mexico Taxation and Revenue Department",
    incomeType: "graduated",
    incomeTaxRange: "1.7% – 5.9%",
    salesTaxRange: "5.125% GRT (+ local)",
    hasStateLevyPower: true,
    difficulty: "hard",
    coordinates: { lat: 34.307144, lng: -106.018066 },
    summary:
      "New Mexico is a NON-SETTLEMENT STATE — no Offer in Compromise and no penalty abatement are available. This makes it one of the most restrictive states for tax resolution. Installment agreements are limited to 12 months maximum with a mandatory 20% down payment. Resolution strategy is essentially limited to compliance and short-term payment plans.",
    keyFacts: [
      "NON-SETTLEMENT STATE — no OIC, no penalty abatement available",
      "Installment agreements limited to 12 months maximum",
      "20% down payment required for all payment plans",
      "New Mexico uses Gross Receipts Tax (GRT) instead of traditional sales tax — applies broadly",
      "Resolution options are extremely limited compared to most states",
    ],
    commonIssues: [
      "The complete absence of OIC and penalty abatement makes New Mexico one of the hardest states for tax resolution",
      "The 12-month maximum with 20% down payment creates a very compressed timeline for paying off balances",
      "Taxpayers with large balances have essentially no settlement options",
      "Professional representation is critical to maximize the limited options available",
    ],
    oic: {
      available: false,
      formNumber: "",
      stopsCollections: false,
      notes: "NON-SETTLEMENT STATE. No OIC and no penalty abatement available.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 12,
      maxMonthsWithFinancials: 12,
      notes: "Maximum 12 months. 20% down payment required.",
    },
    garnishment: null,
    penaltyAbatement: {
      available: false,
      notes: "Not available — New Mexico is a non-settlement state.",
    },
    bankLevy: null,
    hardship: null,
    collectionAgency: null,
    statute: "Unknown",
    recentChanges: [],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#New-Mexico",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },

  {
    name: "New York",
    abbreviation: "NY",
    slug: "state-income-tax-help-new-york",
    capital: "Albany",
    taxAuthority: "New York Department of Taxation and Finance",
    incomeType: "graduated",
    incomeTaxRange: "4% – 10.9% (+ NYC local)",
    salesTaxRange: "4% (+ local)",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 42.165726, lng: -74.948051 },
    summary:
      "New York tax debts are handled by the Department of Taxation and Finance (DTF) and can trigger liens, levies, wage garnishments, tax warrants, and driver's license suspensions. The state's OIC program will stop collections while under review, and installment plans are available up to 36 months without financials. New York has a 20-year collection statute — double the IRS's 10 years. The state uses an aggressive escalation process: voluntary wage assignment first, then mandatory garnishment, then license suspension for repeat defaulters.",
    keyFacts: [
      "OIC available via Forms DTF-4 & DTF-5 — WILL stop collections while under review",
      "Client must be compliant with tax returns for OIC — separate forms required for business and individual if both owe",
      "State asks for pure tax at minimum for individual and sales tax OICs",
      "OIC red flag: consecutive-year debtors deemed 'bad taxpayers' — significant negative factor",
      "Installment agreements (IPAs) up to 36 months without financials",
      "Online application for balances up to $20K — over $20K must call",
      "Financials needed for IAs exceeding 36 months — max term not publicly stated",
      "Wage garnishment (Income Execution): DTF first asks for voluntary 10% of gross wages — if refused, orders employer to garnish",
      "Bank levy freezes account for 21 days then seizes funds — joint accounts vulnerable",
      "Driver's license suspension if owe $10,000+ with 60 days to respond",
      "License suspension exempt: CDL holders, those on payment plans, receiving public assistance/SSI",
      "Default on 2 payment plans in a year triggers license suspension",
      "20-year collection statute from date warrant could first be filed — double the IRS",
      "As of July 2025: all warrants filed electronically with Department of State (previously individual county clerks)",
    ],
    commonIssues: [
      "The 20-year statute means New York debts persist far longer than federal — no waiting it out",
      "Consecutive-year debt history is a major OIC red flag — the state views this as a pattern of non-compliance",
      "The voluntary-then-mandatory garnishment escalation means early engagement is critical",
      "Two payment plan defaults in a year triggers automatic license suspension — compliance is essential",
      "Joint bank accounts are fully vulnerable to the 21-day levy freeze",
      "NYC residents face additional local income tax on top of state rates",
    ],
    oic: {
      available: true,
      formNumber: "Forms DTF-4 & DTF-5",
      stopsCollections: true,
      notes:
        "WILL stop collections. Client must be compliant. Separate DTF-4 & DTF-5 for business AND individual if both owe. State asks for pure tax at minimum. Consecutive-year debt is a negative factor.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 36,
      maxMonthsWithFinancials: 0,
      notes:
        "36 months without financials. Online for balances up to $20K. Over $20K or over 36 months requires calling and financials. Max term not publicly stated.",
    },
    garnishment: {
      canLift: false,
      canReduce: true,
      percentage: "10% of gross (voluntary), then mandatory",
      notes:
        "DTF first asks for voluntary 10%. If refused, orders employer to garnish. Default on 2 plans in a year triggers license suspension.",
    },
    penaltyAbatement: {
      available: true,
      notes: "Considers specific hardship factors.",
    },
    bankLevy: {
      canRelease: false,
      notes: "Freezes account 21 days then seizes. Joint accounts vulnerable.",
    },
    hardship: null,
    collectionAgency: null,
    statute: "20 years",
    recentChanges: [
      "July 2025: All warrants filed electronically with Department of State (previously individual county clerks)",
      "PTET (Pass-Through Entity Tax) continues as SALT cap workaround",
    ],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#New-York",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "North Carolina",
    abbreviation: "NC",
    slug: "state-income-tax-help-north-carolina",
    capital: "Raleigh",
    taxAuthority: "North Carolina Department of Revenue",
    incomeType: "flat",
    incomeTaxRange: "4.5%",
    salesTaxRange: "4.75% (+ local)",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 35.630066, lng: -79.806419 },
    summary:
      "North Carolina tax debts are handled by the Department of Revenue and can trigger liens, levies, and wage garnishments. The state offers installment agreements up to 40 months without financials, and an OIC that will prevent collections if none are already in place. However, enforcement actions are severe: bank levies cannot be released or modified, wage garnishment for 1099 workers can reach 100% of earnings, and defaulting on an IA results in permanent loss of that IA with no reinstatement.",
    keyFacts: [
      "OIC available via Form OIC-1062 — will prevent collections IF no collections already in place",
      "Installment agreements up to 40 months without financials",
      "Over 40 months: Form RO-1062 plus supporting docs (3 bank statements, all income/expenses)",
      "IA default = NO reinstatement — must pay in full or go through collections",
      "However: any balance NOT in the defaulted IA IS eligible for a new IA",
      "Bank levy: NO releases, NO modifications — completely inflexible",
      "W2 wage garnishment: 10% of gross wages — NO reduction",
      "1099 worker garnishment: up to 100% of earnings — NO reduction",
      "Rate reduced to 4.5%, targeting 3.99% by 2027",
    ],
    commonIssues: [
      "IA default is catastrophic — there is no reinstatement, making timely payments absolutely critical",
      "The 100% garnishment rate for 1099 workers is among the most severe nationally",
      "Bank levies are completely inflexible with no modification or release possible",
      "The split between defaulted IA balances (no new IA) and non-IA balances (eligible for new IA) creates complex resolution scenarios",
    ],
    oic: {
      available: true,
      formNumber: "Form OIC-1062",
      stopsCollections: true,
      notes: "Will prevent collections IF no collections are already in place.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 40,
      maxMonthsWithFinancials: 60,
      notes:
        "40 months without financials. Over 40 months: Form RO-1062 plus docs. Default = NO reinstatement. But balances not in defaulted IA are eligible for new IA.",
    },
    garnishment: {
      canLift: false,
      canReduce: false,
      percentage: "W2: 10% gross, 1099: up to 100%",
      notes:
        "No reduction on either. 1099 workers face up to 100% garnishment.",
    },
    penaltyAbatement: { available: true, notes: "" },
    bankLevy: {
      canRelease: false,
      notes: "NO releases, NO modifications — completely inflexible.",
    },
    hardship: null,
    collectionAgency: null,
    statute: "Unknown",
    recentChanges: ["Rate reduced to 4.5%, targeting 3.99% by 2027"],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#North-Carolina",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "North Dakota",
    abbreviation: "ND",
    slug: "state-income-tax-help-north-dakota",
    capital: "Bismarck",
    taxAuthority: "North Dakota Office of State Tax Commissioner",
    incomeType: "flat",
    incomeTaxRange: "1.95%",
    salesTaxRange: "5% (+ local)",
    hasStateLevyPower: true,
    difficulty: "easy",
    coordinates: { lat: 47.528912, lng: -99.784012 },
    summary:
      "North Dakota tax debts are handled by the Office of State Tax Commissioner. With a flat rate of just 1.95% — among the lowest nationally — individual tax liabilities tend to be small. Limited resolution details are publicly available. Collections aggressiveness is generally low.",
    keyFacts: [
      "Flat 1.95% rate — among the lowest state income tax rates nationally",
      "OIC availability unknown — limited public information",
      "Payment plan terms not well documented publicly",
      "Collections aggressiveness is generally low",
      "Low rate means individual tax liabilities tend to be relatively small",
    ],
    commonIssues: [
      "Limited publicly available resolution information",
      "The low rate means most resolution cases are primarily federal, not state",
    ],
    oic: null,
    installmentAgreement: null,
    garnishment: null,
    penaltyAbatement: null,
    bankLevy: null,
    hardship: null,
    collectionAgency: null,
    statute: "Unknown",
    recentChanges: ["Flat 1.95% — among lowest rates nationally"],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#North-Dakota",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Ohio",
    abbreviation: "OH",
    slug: "state-income-tax-help-ohio",
    capital: "Columbus",
    taxAuthority: "Ohio Department of Taxation",
    incomeType: "graduated",
    incomeTaxRange: "0% – 3.5%",
    salesTaxRange: "5.75% (+ local)",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 40.388783, lng: -82.764915 },
    summary:
      "Ohio tax debts are handled by the Department of Taxation and can trigger liens, levies, and wage garnishments. The state offers an OIC but the form must be requested directly — it isn't published. IAs will NOT lift garnishments or levies. Ohio uses three different collection agencies, each with different terms and timelines. A unique complication: Ohio also has municipal income taxes collected by independent agencies, creating a multi-layer environment.",
    keyFacts: [
      "OIC available BUT form must be requested from the state — not published online",
      "OIC will NOT stop collections",
      "Must include income vs. expense table (page 4 of IRS 433-A acceptable — full 433-A not needed)",
      "All documents must be submitted or package returned — incomplete applications rejected",
      "Installment agreements up to 12 months without financials",
      "Over 12 months: letter of hardship plus supporting docs required",
      "IA will NOT lift garnishment or levy",
      "Three collection agencies with different terms: Reliant Capital (max 9 months, no exceptions), United Collection Bureau (12-18 months, 1/3 DP suggested), Pohler & Keck (24 months, annual re-review)",
      "Ohio also has municipal income taxes with independent collection — multi-layer environment",
      "First $26K of income untaxed, rates 0%-3.5%",
    ],
    commonIssues: [
      "The unpublished OIC form creates an extra hurdle — taxpayers must know to request it",
      "Three different collection agencies with completely different terms means outcomes depend heavily on which agency holds the case",
      "Reliant Capital's hard 9-month maximum is the strictest of any Ohio collection agency",
      "Municipal income taxes add another layer of potential debt and enforcement beyond the state level",
      "IAs cannot lift existing garnishments or levies — these must be addressed separately",
    ],
    oic: {
      available: true,
      formNumber: "Must be requested from state",
      stopsCollections: false,
      notes:
        "Form not published online — must request. Will NOT stop collections. Include page 4 of IRS 433-A. All docs must be complete or package returned.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 12,
      maxMonthsWithFinancials: 24,
      notes:
        "12 months without financials. Over 12 months: hardship letter plus supporting docs. IA will NOT lift garnishment or levy.",
    },
    garnishment: {
      canLift: false,
      canReduce: false,
      percentage: "Varies",
      notes: "IA will NOT lift garnishment.",
    },
    penaltyAbatement: { available: true, notes: "" },
    bankLevy: { canRelease: false, notes: "" },
    hardship: null,
    collectionAgency: {
      name: "Reliant Capital / United Collection Bureau / Pohler & Keck",
      notes:
        "Reliant: max 9 months, no exceptions. UCB: 12-18 months, 1/3 DP suggested. Pohler & Keck: 24 months, annual re-review.",
    },
    statute: "Unknown",
    recentChanges: ["First $26K untaxed, rates 0%-3.5%"],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Ohio",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Oklahoma",
    abbreviation: "OK",
    slug: "state-income-tax-help-oklahoma",
    capital: "Oklahoma City",
    taxAuthority: "Oklahoma Tax Commission",
    incomeType: "graduated",
    incomeTaxRange: "0.25% – 4.75%",
    salesTaxRange: "4.5% (+ local)",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 35.007752, lng: -97.092877 },
    summary:
      "Oklahoma tax debts are handled by the Tax Commission and can trigger liens, levies, wage garnishments, and tax warrants. Most cases are referred to third-party collection agencies, and once referred, debts cannot be returned to the state. The state has a 'Waiver Program' instead of a traditional OIC — it requires lump sum payment within 30 days and takes 6-12 months for review. Wage garnishment reduction is possible but extremely strict — any discretionary spending visible on bank statements will result in denial.",
    keyFacts: [
      "OIC (Waiver Program): lump sum must be paid within 30 days of applying",
      "Written letter explaining why penalties and interest should be waived plus hardship justification",
      "6-12 month review time for waiver — if accepted, must pay tax warrant fee and lien fee",
      "Most cases referred to third-party agencies (Linebarger, Harris & Harris, GC Services, Municipal Services Bureau)",
      "Once with collection agency, debts CANNOT be returned to Oklahoma",
      "Harris & Harris: 9-12 month IAs, 18 months for extreme circumstances, DP required if balance over $5K and over 9 months",
      "LGBS: up to 12 months with 30% DP, or up to 36 months with 10% DP (lien filed)",
      "Wage garnishment cannot be lifted through IA — reduction possible with full financials",
      "Garnishment reduction will be DENIED if bank statements show discretionary spending (eating out, streaming services, etc.)",
      "No specific collection statute — debts persist",
    ],
    commonIssues: [
      "The discretionary spending scrutiny on garnishment reduction is among the strictest nationally — any non-essential spending is grounds for denial",
      "Once a debt is with a collection agency it cannot be returned to the state — you're locked in with that agency's terms",
      "The 30-day lump sum requirement for the Waiver Program makes it inaccessible for most cash-strapped taxpayers",
      "Multiple collection agencies with very different terms create inconsistent outcomes",
    ],
    oic: {
      available: true,
      formNumber: "Waiver Program",
      stopsCollections: false,
      notes:
        "Lump sum within 30 days. Written hardship letter. 6-12 month review. If accepted, must pay warrant and lien fees.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 36,
      maxMonthsWithFinancials: 36,
      notes:
        "Most cases go to collection agencies. Harris & Harris: 9-18 months. LGBS: 12 months with 30% DP or 36 months with 10% DP.",
    },
    garnishment: {
      canLift: false,
      canReduce: true,
      percentage: "Varies",
      notes:
        "Cannot lift via IA. Reduction requires full financials, 3 pay stubs, 3 bank statements. Denied if bank statements show discretionary spending (eating out, streaming services, etc.).",
    },
    penaltyAbatement: { available: true, notes: "" },
    bankLevy: { canRelease: false, notes: "" },
    hardship: null,
    collectionAgency: {
      name: "Harris & Harris / LGBS / GC Services / Municipal Services Bureau",
      notes:
        "Cannot return debts from agency to state. Each agency has different terms.",
    },
    statute: "No limit",
    recentChanges: [],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Oklahoma",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Oregon",
    abbreviation: "OR",
    slug: "state-income-tax-help-oregon",
    capital: "Salem",
    taxAuthority: "Oregon Department of Revenue",
    incomeType: "graduated",
    incomeTaxRange: "4.75% – 9.9%",
    salesTaxRange: "No state sales tax",
    hasStateLevyPower: true,
    difficulty: "hard",
    coordinates: { lat: 43.804133, lng: -120.554201 },
    summary:
      "Oregon is the only state in the union where your tax preparer must reside within the state, making it impossible for us to help you file and manage payment plans. Oregon tax debts are handled by the Department of Revenue and can trigger liens, levies, and wage garnishments. Limited resolution details are publicly available for Oregon's specific programs. The state has no sales tax, so tax issues are primarily income-tax related. Oregon also imposes a Corporate Activity Tax on businesses. Collections aggressiveness is generally low. The collection statute is unlimited — debts persist indefinitely.",
    keyFacts: [
      "OIC availability unknown — limited public information",
      "Payment plan terms not well documented publicly",
      "No collection statute — debts persist indefinitely",
      "No state sales tax — tax issues are primarily income-related",
      "Corporate Activity Tax (0.57%) applies to businesses",
      "9.9% top rate is among the highest nationally",
      "Collections aggressiveness is generally low",
    ],
    commonIssues: [
      "No collection statute means Oregon debts never expire",
      "Limited publicly available resolution information — professional representation recommended",
      "The 9.9% top rate creates significant state-level liabilities for high-income taxpayers",
    ],
    oic: {
      available: false,
      formNumber: "",
      stopsCollections: false,
      notes: "Availability unknown — limited public information.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 0,
      maxMonthsWithFinancials: 0,
      notes: "Terms not well documented publicly.",
    },
    garnishment: null,
    penaltyAbatement: { available: true, notes: "" },
    bankLevy: null,
    hardship: null,
    collectionAgency: null,
    statute: "No limit",
    recentChanges: ["Corporate Activity Tax (0.57%) continues"],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Oregon",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Pennsylvania",
    abbreviation: "PA",
    slug: "state-income-tax-help-pennsylvania",
    capital: "Harrisburg",
    taxAuthority: "Pennsylvania Department of Revenue",
    incomeType: "flat",
    incomeTaxRange: "3.07%",
    salesTaxRange: "6% (+ local)",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 41.203322, lng: -77.194525 },
    summary:
      "Pennsylvania tax debts are handled by the Department of Revenue and can trigger liens, levies, and wage garnishments. The state offers an OIC through the Board of Appeals with strict rules, and installment agreements up to 48 months with financials. IAs will NOT lift garnishments or levies. Any IA beyond 6 months triggers a lien filing, and balances over $1,000 require direct debit. Pennsylvania also has extensive local earned income taxes collected by separate agencies, adding complexity.",
    keyFacts: [
      "OIC available through Board of Appeals — very strict rules",
      "Installment agreements up to 24 months without financials",
      "Up to 48 months with financials (Forms REV-488 for individuals or REV-484 for businesses)",
      "IA will NOT lift garnishment or levy",
      "Any IA beyond 6 months = lien WILL be filed",
      "Balance over $1,000 = payment plan requires Direct Debit (Form REV-692)",
      "No collection statute — debts persist indefinitely",
      "Extensive local earned income taxes collected by separate agencies (Keystone Collections, Berkheimer, etc.)",
      "Rate unchanged at 3.07% — one of the lowest flat rates nationally",
    ],
    commonIssues: [
      "The 6-month lien threshold means most payment plans result in a public lien filing",
      "Direct debit requirement for balances over $1,000 limits payment flexibility",
      "Local earned income taxes create a separate layer of debt and enforcement beyond the state level",
      "No collection statute means debts persist indefinitely",
      "The Board of Appeals OIC process is very strict — not a high-success-rate path",
    ],
    oic: {
      available: true,
      formNumber: "Board of Appeals",
      stopsCollections: false,
      notes: "Very strict rules. Through Board of Appeals.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 24,
      maxMonthsWithFinancials: 48,
      notes:
        "24 months without financials. Up to 48 months with Forms REV-488 (individual) or REV-484 (business). IA will NOT lift garnishment or levy. IAs over 6 months = lien filed. Balance over $1K = direct debit required (Form REV-692).",
    },
    garnishment: {
      canLift: false,
      canReduce: false,
      percentage: "Varies",
      notes: "IA will NOT lift garnishment or levy.",
    },
    penaltyAbatement: { available: true, notes: "" },
    bankLevy: { canRelease: false, notes: "" },
    hardship: null,
    collectionAgency: null,
    statute: "No limit",
    recentChanges: ["Rate unchanged at 3.07%"],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Pennsylvania",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Rhode Island",
    abbreviation: "RI",
    slug: "state-income-tax-help-rhode-island",
    capital: "Providence",
    taxAuthority: "Rhode Island Division of Taxation",
    incomeType: "graduated",
    incomeTaxRange: "3.75% – 5.99%",
    salesTaxRange: "7%",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 41.580095, lng: -71.477429 },
    summary:
      "Rhode Island tax debts are handled by the Division of Taxation and can trigger liens, levies, and wage garnishments. Resolution options are notably restrictive: installment agreements without financials are limited to just 3 months, IAs will NOT lift garnishments or levies, and garnishment is set at a flat $75 per check. The most unusual rule: taxpayers can only get on a payment plan AFTER half the balance has been paid — either by paying half up front or riding out the garnishment until it covers half.",
    keyFacts: [
      "OIC available via Form RI-656 — will NOT stop collections",
      "Installment agreements just 3 months without financials",
      "Over 3 months: Form RI433A plus supporting docs (3 months bank statements, all income/expenses)",
      "IA will NOT lift garnishment or levy",
      "Wage garnishment set at flat $75 per check",
      "Payment plan only available AFTER half the balance is paid — either pay half up front or ride garnishment until halfway",
      "No collection statute — debts persist indefinitely",
    ],
    commonIssues: [
      "The half-balance payment requirement before a plan is available is one of the most restrictive rules nationally",
      "The 3-month without-financials limit is among the shortest — most taxpayers need full documentation",
      "$75 per check garnishment is flat regardless of income, which can be particularly harsh on lower earners",
      "No collection statute means debts never expire",
    ],
    oic: {
      available: true,
      formNumber: "Form RI-656",
      stopsCollections: false,
      notes: "Will NOT stop collections.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 3,
      maxMonthsWithFinancials: 12,
      notes:
        "3 months without financials. Over 3 months: Form RI433A plus docs. IA will NOT lift garnishment or levy. Plan only available AFTER half balance paid.",
    },
    garnishment: {
      canLift: false,
      canReduce: false,
      percentage: "$75 per check",
      notes:
        "Flat $75 per check. Can get on payment plan ONLY after half the balance is paid — either pay half up front or ride out garnishment.",
    },
    penaltyAbatement: { available: true, notes: "" },
    bankLevy: { canRelease: false, notes: "" },
    hardship: null,
    collectionAgency: null,
    statute: "No limit",
    recentChanges: [],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Rhode-Island",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "South Carolina",
    abbreviation: "SC",
    slug: "state-income-tax-help-south-carolina",
    capital: "Columbia",
    taxAuthority: "South Carolina Department of Revenue",
    incomeType: "graduated",
    incomeTaxRange: "0% – 6.4%",
    salesTaxRange: "6% (+ local)",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 33.836081, lng: -81.163725 },
    summary:
      "South Carolina tax debts are handled by the Department of Revenue and can trigger liens, levies, and wage garnishments. The state's OIC will stop collections while under review. Installment agreements can be set up online (10% DP waived, $45 fee) or via form (10% DP plus $45 fee required). Taxpayers on wage garnishment are NOT eligible for an IA — garnishment reduction must be pursued through the Central Levy Department with extensive documentation.",
    keyFacts: [
      "OIC available (form must be requested from state) — WILL stop collections",
      "Online IA setup: 10% down payment waived but $45 IA fee required",
      "Form FS-102 IA: 10% down payment PLUS $45 fee required",
      "Lower DP or longer IA available with written hardship plus supporting docs (3 months bank statements, income/expenses)",
      "Clients on wage garnishment are NOT eligible for IA",
      "Garnishment reduction through Central Levy Department requires extensive documentation",
      "Reduction docs: letter of financial hardship with SSN, full name, payroll fax number, last 2 pay stubs, major bills from last 30 days, monthly expenses, last 2 bank statements",
      "Top rate reduced to 6.4%",
    ],
    commonIssues: [
      "Wage garnishment and installment agreements are mutually exclusive — once garnished, the IA path is closed",
      "The garnishment reduction documentation requirements are among the most extensive of any state",
      "The online vs. form IA difference in down payment requirements can catch taxpayers off guard",
      "Taxpayers should set up an IA before garnishment to preserve that option",
    ],
    oic: {
      available: true,
      formNumber: "Must request from state",
      stopsCollections: true,
      notes: "WILL stop collections.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 12,
      maxMonthsWithFinancials: 24,
      notes:
        "Online: 10% DP waived, $45 fee. Form FS-102: 10% DP plus $45 fee. Lower DP/longer IA with written hardship plus docs.",
    },
    garnishment: {
      canLift: false,
      canReduce: true,
      percentage: "Varies",
      notes:
        "Not eligible for IA if on garnishment. Contact Central Levy Department. Reduction requires extensive documentation: hardship letter, SSN, full name, payroll fax, 2 pay stubs, major bills, monthly expenses, 2 bank statements.",
    },
    penaltyAbatement: { available: true, notes: "" },
    bankLevy: { canRelease: false, notes: "" },
    hardship: null,
    collectionAgency: null,
    statute: "Unknown",
    recentChanges: ["Top rate reduced to 6.4%"],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#South-Carolina",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },

  {
    name: "South Dakota",
    abbreviation: "SD",
    slug: "state-income-tax-help-south-dakota",
    capital: "Pierre",
    taxAuthority: "South Dakota Department of Revenue",
    incomeType: "none",
    incomeTaxRange: "No state income tax",
    salesTaxRange: "4.5% (+ local)",
    hasStateLevyPower: false,
    difficulty: "none",
    coordinates: { lat: 43.969515, lng: -99.901813 },
    summary:
      "South Dakota does not levy a state individual income tax. State tax issues are more commonly tied to business taxes including sales/use tax and excise taxes. Individual residents primarily face federal tax obligations, and the IRS maintains full collection authority regardless of South Dakota's lack of state income tax.",
    keyFacts: [
      "No state individual income tax",
      "State tax issues primarily business-related (sales/use, excise)",
      "Federal tax obligations apply fully — IRS maintains collection authority",
      "4.5% state sales tax plus local additions",
    ],
    commonIssues: [
      "Businesses face sales tax enforcement from the state DOR",
      "Federal tax debts remain the primary concern for individual residents",
    ],
    oic: null,
    installmentAgreement: null,
    garnishment: null,
    penaltyAbatement: null,
    bankLevy: null,
    hardship: null,
    collectionAgency: null,
    statute: "N/A",
    recentChanges: [],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#South-Dakota",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Tennessee",
    abbreviation: "TN",
    slug: "state-income-tax-help-tennessee",
    capital: "Nashville",
    taxAuthority: "Tennessee Department of Revenue",
    incomeType: "none",
    incomeTaxRange: "No state income tax",
    salesTaxRange: "7% (+ local)",
    hasStateLevyPower: false,
    difficulty: "none",
    coordinates: { lat: 35.517491, lng: -86.580447 },
    summary:
      "Tennessee does not levy a state individual income tax. The Hall Tax on interest and dividends was fully repealed in 2021, eliminating the last form of individual income taxation. State tax issues are primarily tied to business taxes including sales/use tax (among the highest nationally at 7% plus local) and franchise/excise taxes. Individual residents primarily face federal tax obligations.",
    keyFacts: [
      "No state individual income tax",
      "Hall Tax (interest & dividends) fully repealed in 2021 — zero individual income tax",
      "7% state sales tax plus local additions — among the highest combined sales tax rates nationally",
      "State tax issues primarily business-related (sales/use, franchise, excise)",
      "Federal tax obligations apply fully — IRS maintains collection authority",
    ],
    commonIssues: [
      "Businesses face aggressive sales tax enforcement given the high combined rates",
      "Federal tax debts remain the primary concern for individual residents",
      "The high sales tax rate creates significant compliance burden for businesses with Tennessee nexus",
    ],
    oic: null,
    installmentAgreement: null,
    garnishment: null,
    penaltyAbatement: null,
    bankLevy: null,
    hardship: null,
    collectionAgency: null,
    statute: "N/A",
    recentChanges: [
      "Hall Tax fully repealed 2021 — zero individual income tax",
    ],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Tennessee",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Texas",
    abbreviation: "TX",
    slug: "state-income-tax-help-texas",
    capital: "Austin",
    taxAuthority: "Texas Comptroller of Public Accounts",
    incomeType: "none",
    incomeTaxRange: "No state income tax",
    salesTaxRange: "6.25% (+ local)",
    hasStateLevyPower: false,
    difficulty: "hard",
    coordinates: { lat: 31.968599, lng: -99.90181 },
    summary:
      "Texas does not levy a state individual income tax — it is constitutionally prohibited. However, the Texas Comptroller is notably aggressive on business tax collections (sales/use, franchise tax). There is no OIC program and no settlement option. Payment plans require a 25% down payment with a 10-day substantiation deadline and zero flexibility. Texas generally prohibits wage garnishment for tax debt, but the Comptroller can levy bank accounts and seize business assets.",
    keyFacts: [
      "No state individual income tax — constitutionally prohibited",
      "NO OIC program — Texas does not offer settlements",
      "Installment agreements require 25% down payment first",
      "After DP, taxpayer may request IA — Comptroller mails a list of required documents",
      "10-day deadline to provide substantiation — zero flexibility if missed",
      "Documents reviewed by management, approved by headquarters",
      "Texas generally prohibits wage garnishment for tax (exceptions: child support, student loans, IRS levies)",
      "Comptroller CAN levy bank accounts and seize business assets",
      "4-year assessment statute, but 20 years for liens",
      "2024: Franchise tax no-tax-due threshold raised to $2.47M — thousands of small businesses exempted",
      "Collections aggressiveness on business taxes is high despite no income tax",
    ],
    commonIssues: [
      "The 25% down payment plus 10-day documentation deadline creates an extremely compressed timeline — missing it means losing the IA option",
      "Zero flexibility on deadlines and documentation requirements — the Comptroller does not negotiate",
      "While individuals don't face income tax, businesses face aggressive franchise and sales tax enforcement",
      "Bank account levies are the primary enforcement tool since wage garnishment is generally prohibited",
      "The 20-year lien statute means business tax liens persist for decades",
    ],
    oic: {
      available: false,
      formNumber: "",
      stopsCollections: false,
      notes: "Texas does not offer OIC or settlements.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 0,
      maxMonthsWithFinancials: 0,
      notes:
        "25% down payment required. 10-day deadline for substantiation. Documents reviewed by management, approved by headquarters. Zero flexibility.",
    },
    garnishment: {
      canLift: false,
      canReduce: false,
      percentage: "N/A",
      notes:
        "Texas generally prohibits wage garnishment for tax. Comptroller can levy bank accounts and seize business assets.",
    },
    penaltyAbatement: { available: false, notes: "" },
    bankLevy: {
      canRelease: false,
      notes: "Primary enforcement tool since wage garnishment is prohibited.",
    },
    hardship: null,
    collectionAgency: null,
    statute: "4 years assessment, 20 years liens",
    recentChanges: [
      "2024: Franchise tax no-tax-due threshold raised to $2.47M (SB 3) — thousands of small businesses exempted",
      "2025-2026: 89th Legislature made additional franchise tax changes",
      "No state income tax (constitutionally prohibited)",
    ],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Texas",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Utah",
    abbreviation: "UT",
    slug: "state-income-tax-help-utah",
    capital: "Salt Lake City",
    taxAuthority: "Utah State Tax Commission",
    incomeType: "flat",
    incomeTaxRange: "4.65%",
    salesTaxRange: "6.1% (+ local)",
    hasStateLevyPower: true,
    difficulty: "easy",
    coordinates: { lat: 39.32098, lng: -111.093731 },
    summary:
      "Utah tax debts are handled by the State Tax Commission and can trigger liens, levies, and wage garnishments. Utah is one of the more taxpayer-friendly states for resolution — installment agreements are available up to 48 months without financials, and direct debit is NOT required. The state uses GC Services as its collection agency. OIC is available.",
    keyFacts: [
      "OIC available — terms handled case-by-case",
      "Installment agreements up to 48 months without financials",
      "Direct debit NOT required — unusual flexibility",
      "Collection agency (GC Services): up to 24 months without financials, over 24 months requires full financials (Form TC805)",
      "Rate reduced to 4.65% (from 4.85%)",
      "Collections aggressiveness is generally low",
    ],
    commonIssues: [
      "GC Services has different terms than the state directly — 24-month cap without financials vs. state's 48 months",
      "Taxpayers should try to resolve before referral to GC Services to get the more favorable state terms",
    ],
    oic: {
      available: true,
      formNumber: "",
      stopsCollections: false,
      notes: "Available — terms handled case-by-case.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 48,
      maxMonthsWithFinancials: 60,
      notes: "48 months without financials. Direct debit NOT required.",
    },
    garnishment: null,
    penaltyAbatement: { available: true, notes: "" },
    bankLevy: null,
    hardship: null,
    collectionAgency: {
      name: "GC Services",
      notes:
        "24 months without financials. Over 24 months: full financials (Form TC805).",
    },
    statute: "Unknown",
    recentChanges: ["Rate reduced to 4.65% (from 4.85%)"],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Utah",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Vermont",
    abbreviation: "VT",
    slug: "state-income-tax-help-vermont",
    capital: "Montpelier",
    taxAuthority: "Vermont Department of Taxes",
    incomeType: "graduated",
    incomeTaxRange: "3.35% – 8.75%",
    salesTaxRange: "6% (+ local)",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 44.558803, lng: -72.577841 },
    summary:
      "Vermont tax debts are handled by the Department of Taxes and can trigger liens, levies, and wage garnishments. Limited resolution details are publicly available for Vermont's specific programs. Collections aggressiveness is generally low.",
    keyFacts: [
      "OIC availability unknown — limited public information",
      "Payment plan terms not well documented publicly",
      "Collection statute details unclear",
      "Collections aggressiveness is generally low",
      "Rates range from 3.35% to 8.75%",
    ],
    commonIssues: [
      "Limited publicly available resolution information — professional representation recommended",
    ],
    oic: null,
    installmentAgreement: null,
    garnishment: null,
    penaltyAbatement: null,
    bankLevy: null,
    hardship: null,
    collectionAgency: null,
    statute: "Unknown",
    recentChanges: [],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Vermont",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Virginia",
    abbreviation: "VA",
    slug: "state-income-tax-help-virginia",
    capital: "Richmond",
    taxAuthority: "Virginia Department of Taxation",
    incomeType: "graduated",
    incomeTaxRange: "2% – 5.75%",
    salesTaxRange: "5.3% (+ local)",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 37.431573, lng: -78.656894 },
    summary:
      "Virginia tax debts are handled by the Department of Taxation and can trigger liens, levies, and wage garnishments. The state's OIC program will stop collections while under review. Installment agreements are available up to 12 months without financials. IAs will NOT lift garnishments, but the state is notably responsive on garnishment modifications — agents can fax a modification to the employer the same day. Virginia has a 20-year statute for tax liens.",
    keyFacts: [
      "OIC available via Form 21-OIC-IND — WILL stop collections",
      "Installment agreements up to 12 months without financials",
      "Over 12 months: complete last 2 pages of OIC form for financial disclosure",
      "Client must be compliant with ALL returns for any resolution",
      "IA will NOT lift garnishment or levy",
      "Garnishment modification: provide pay frequency and payroll fax number — agent faxes modification to employer same day",
      "20-year statute for state tax liens",
      "State does NOT have W2 information unless client filed an actual return",
    ],
    commonIssues: [
      "The 12-month without-financials limit is relatively short — many taxpayers need the extended option",
      "IAs do not lift garnishments, but the same-day modification process is faster than most states",
      "The state's lack of W2 data without a filed return can complicate verification of income and withholding",
      "20-year lien statute means Virginia debts persist nearly as long as NY and CA",
    ],
    oic: {
      available: true,
      formNumber: "Form 21-OIC-IND",
      stopsCollections: true,
      notes: "WILL stop collections.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 12,
      maxMonthsWithFinancials: 24,
      notes:
        "12 months without financials. Over 12 months: complete last 2 pages of OIC form. Must be compliant with ALL returns. IA will NOT lift garnishment.",
    },
    garnishment: {
      canLift: false,
      canReduce: true,
      percentage: "Varies",
      notes:
        "Provide pay frequency and payroll fax number. Agent faxes modification to employer same day.",
    },
    penaltyAbatement: { available: true, notes: "" },
    bankLevy: { canRelease: false, notes: "" },
    hardship: null,
    collectionAgency: null,
    statute: "20 years",
    recentChanges: ["Rates unchanged (2%-5.75%)"],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Virginia",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Washington",
    abbreviation: "WA",
    slug: "state-income-tax-help-washington",
    capital: "Olympia",
    taxAuthority: "Washington Department of Revenue",
    incomeType: "none",
    incomeTaxRange: "No state income tax",
    salesTaxRange: "6.5% (+ local)",
    hasStateLevyPower: false,
    difficulty: "moderate",
    coordinates: { lat: 47.751074, lng: -120.740139 },
    summary:
      "Washington does not levy a traditional state income tax on wages. However, since 2022 the state imposes a 7% capital gains tax on gains exceeding $270K (upheld by courts in 2023). The Business & Occupation (B&O) Tax applies to businesses regardless of profitability. State tax issues are primarily tied to sales/use tax, B&O tax, and the capital gains tax for high earners.",
    keyFacts: [
      "No state income tax on wages",
      "7% capital gains tax on gains exceeding $270K (upheld by courts in 2023)",
      "Business & Occupation (B&O) Tax applies regardless of profitability",
      "6.5% state sales tax plus local additions — combined rates among the highest nationally",
      "Federal tax obligations apply fully — IRS maintains collection authority",
      "State tax issues primarily business-related plus capital gains for high earners",
    ],
    commonIssues: [
      "The capital gains tax is relatively new and creates state-level liability for taxpayers who previously had none",
      "B&O Tax applies to gross receipts regardless of whether the business is profitable — unique enforcement concern",
      "High combined sales tax rates create significant compliance burden for businesses",
    ],
    oic: null,
    installmentAgreement: null,
    garnishment: null,
    penaltyAbatement: null,
    bankLevy: null,
    hardship: null,
    collectionAgency: null,
    statute: "N/A",
    recentChanges: [
      "7% capital gains tax on gains over $270K (upheld 2023)",
      "B&O Tax applies regardless of profitability",
    ],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Washington-state",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "West Virginia",
    abbreviation: "WV",
    slug: "state-income-tax-help-west-virginia",
    capital: "Charleston",
    taxAuthority: "West Virginia Tax Division",
    incomeType: "graduated",
    incomeTaxRange: "2.36% – 5.12%",
    salesTaxRange: "6% (+ local)",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 38.597626, lng: -80.454903 },
    summary:
      "West Virginia tax debts are handled by the Tax Division and can trigger liens, levies, and wage garnishments. The state's OIC program (Form CD-3) will stop collections while under review. Installment agreements are limited to 12 months maximum with a lien filed on any IA, though plans of 6 months or less avoid the lien. Missing returns require a written request explaining why the state should accept late filings.",
    keyFacts: [
      "OIC available via Form CD-3 — WILL stop collections",
      "WV-433-A (financial statement) must accompany the CD-3",
      "Installment agreements up to 12 months maximum (Form CD-5)",
      "Lien filed on any IA — but IAs of 6 months or less avoid the lien",
      "Missing returns: client must send written request explaining why state should accept late returns",
      "State is targeting eventual elimination of income tax",
    ],
    commonIssues: [
      "The 12-month IA maximum is restrictive for larger balances",
      "Liens are filed on most IAs — only the shortest plans (6 months or less) avoid it",
      "The written explanation requirement for late returns adds an extra step to the compliance process",
    ],
    oic: {
      available: true,
      formNumber: "Form CD-3",
      stopsCollections: true,
      notes: "WILL stop collections. WV-433-A must accompany.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 12,
      maxMonthsWithFinancials: 12,
      notes:
        "12 months max (Form CD-5). Lien filed on any IA. IAs of 6 months or less = no lien.",
    },
    garnishment: null,
    penaltyAbatement: { available: true, notes: "" },
    bankLevy: null,
    hardship: null,
    collectionAgency: null,
    statute: "Unknown",
    recentChanges: ["Targeting eventual elimination of state income tax"],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#West-Virginia",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Wisconsin",
    abbreviation: "WI",
    slug: "state-income-tax-help-wisconsin",
    capital: "Madison",
    taxAuthority: "Wisconsin Department of Revenue",
    incomeType: "graduated",
    incomeTaxRange: "3.5% – 7.65%",
    salesTaxRange: "5% (+ local)",
    hasStateLevyPower: true,
    difficulty: "moderate",
    coordinates: { lat: 43.78444, lng: -88.787868 },
    summary:
      "Wisconsin tax debts are handled by the Department of Revenue and can trigger liens, levies, and wage garnishments. The state's OIC program (Form A-212) will stop collections, but the client must get on an IA while the OIC is being reviewed. IAs are available up to 24 months without financials, but will NOT lift garnishments or levies. Wage garnishment reduction is possible via Form A-772. Bank levy release is possible with specific documentation. The collection statute is 20 years.",
    keyFacts: [
      "OIC available via Form A-212 — WILL stop collections",
      "Client must get on an IA while OIC is being reviewed — dual track required",
      "Installment agreements up to 24 months without financials",
      "Over 24 months: Form A-771 plus supporting docs (3 months financials, all income/expenses)",
      "IA will NOT lift garnishment or levy",
      "Wage garnishment reduction possible via Form A-772",
      "Bank levy release possible: requires Form A-771, EFT form, 60 days of bank statements, and screenshot with exact amount on hold",
      "20-year collection statute for liens",
    ],
    commonIssues: [
      "The dual-track OIC requirement (must be on an IA simultaneously) means taxpayers need to manage two processes at once",
      "IAs do not lift garnishments — garnishment reduction must be pursued separately via Form A-772",
      "Bank levy release is possible but requires very specific documentation — the screenshot of the hold amount is a unique requirement",
      "20-year lien statute means debts persist for decades",
    ],
    oic: {
      available: true,
      formNumber: "Form A-212",
      stopsCollections: true,
      notes:
        "WILL stop collections. Must get on IA while OIC is being reviewed.",
    },
    installmentAgreement: {
      maxMonthsNoFinancials: 24,
      maxMonthsWithFinancials: 48,
      notes:
        "24 months without financials. Over 24 months: Form A-771 plus docs. IA will NOT lift garnishment or levy.",
    },
    garnishment: {
      canLift: false,
      canReduce: true,
      percentage: "Varies",
      notes: "Reduction via Form A-772.",
    },
    penaltyAbatement: { available: true, notes: "" },
    bankLevy: {
      canRelease: true,
      notes:
        "Requires Form A-771, EFT form, 60 days bank statements, and screenshot with exact hold amount.",
    },
    hardship: null,
    collectionAgency: null,
    statute: "20 years",
    recentChanges: ["Rates 3.5%-7.65%, reform discussions ongoing"],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Wisconsin",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
  {
    name: "Wyoming",
    abbreviation: "WY",
    slug: "state-income-tax-help-wyoming",
    capital: "Cheyenne",
    taxAuthority: "Wyoming Department of Revenue",
    incomeType: "none",
    incomeTaxRange: "No state income tax",
    salesTaxRange: "4% (+ local)",
    hasStateLevyPower: false,
    difficulty: "none",
    coordinates: { lat: 43.075968, lng: -107.290284 },
    summary:
      "Wyoming does not levy a state individual income tax. State tax issues are primarily tied to business taxes including sales/use tax and mineral severance taxes. Individual residents primarily face federal tax obligations. Collections aggressiveness is generally low.",
    keyFacts: [
      "No state individual income tax",
      "State tax issues primarily business-related (sales/use, mineral severance)",
      "Federal tax obligations apply fully — IRS maintains collection authority",
      "4% state sales tax plus local additions",
      "Mineral severance taxes are a significant revenue source and potential issue for energy-sector businesses",
    ],
    commonIssues: [
      "Businesses in the energy and mineral extraction sectors face severance tax compliance issues",
      "Federal tax debts remain the primary concern for individual residents",
    ],
    oic: null,
    installmentAgreement: null,
    garnishment: null,
    penaltyAbatement: null,
    bankLevy: null,
    hardship: null,
    collectionAgency: null,
    statute: "N/A",
    recentChanges: [],
    officialLinks: [
      {
        label: "State Tax Overview (Public Integrity)",
        url: "https://publicintegrity.org/inequality-poverty-opportunity/taxes/unequal-burden/behind-on-state-income-taxes-heres-what-you-need-to-know/#Wyoming",
      },
      {
        label: "NCSL State Tax Actions Database",
        url: "https://www.ncsl.org/fiscal/state-tax-actions-database",
      },
    ],
  },
];

// ─── Helper Functions ───

const getStateBySlug = (slug) => stateData.find((s) => s.slug === slug);
const getStateByAbbreviation = (abbr) =>
  stateData.find((s) => s.abbreviation === abbr.toUpperCase());
const getNoIncomeTaxStates = () =>
  stateData.filter((s) => s.incomeType === "none");
const getDifficultyStates = (level) =>
  stateData.filter((s) => s.difficulty === level);

// ─── Exports (CRA import + Node require compatible) ───

export default stateData;
export {
  stateData,
  getStateBySlug,
  getStateByAbbreviation,
  getNoIncomeTaxStates,
  getDifficultyStates,
};
